import { Line2d } from '../../math/Line2d';
import { LineSeg2d } from '../../math/LineSeg2d';
import { Point2dUtils } from '../../math/Point2dUtils';
import { PathFinder } from '../../model/polygon/Region';
import { EPointOnSegmentType } from '../../types';
import { IWallInfo } from '@/editor/types/wallShape';
import { getNearestPoint } from '@/editor/math/utils';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { getShapeSeg, isRightAngleShape, isTangentShape } from '@/editor/math/shape/other';
import { IShape2d, IShapeSeg2d } from '@/editor/types/shape';
import { Ray2d } from '@/editor/math/ray2d';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { getWallDrawShape, reverseShape } from '.';
import { IShapePoint2d } from '@/editor/types/shape';
import { getTangentDirection } from '@/editor/math/shape/tangent';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { arcThresholdValue, thresholdValue } from '@/editor/math/const';
import { Circle2d } from '@/editor/math/circle2d';
import { HandleType } from '../wallShape/calculateHelper';
import {
  bodyDirectionOf,
  crossOf,
  endCapFaceAt,
  endOf,
  extendSeg,
  farEndOf,
  isCollinear,
  isEndpointOf,
  isInsideBand,
  isNearHairpinJoint,
  isStrictlyInsideBand,
  markCapArcs,
  nearestWithinReach,
  nearOffsetSegOf,
  onSegSpan,
  projectAlong,
  projT,
  pushCapPoint,
  rayIntersectSegments,
  sharedCornerWithin,
  sharesBothEndpoints,
  signedDistanceToLine,
  signedSideOfCenterline,
  strictlyOnSeg,
  sweepOnArc
} from './geometry';
import { extendedSegIntersectSegs } from './segIntersect';
import { calArcJunctionCap } from './arcJunctionCap';
import { connectToPointOf, IWallLookup, neighborsOf, onWallOf } from './wallLookup';

interface IUpdateItem {
  wallId: string;
  type: HandleType;
  wallIndex: number;
}

export class CalculateHelper implements IWallLookup {
  // 存储所有关联墙体数据，用于递归处理相邻墙体端点
  protected _cacheWallMap: Map<string, IWallInfo> = new Map();
  // 墙体真实建立顺序(来自 _wallInfos 的下标)，与缓存插入顺序无关。用于「先绘制切后绘制」的稳定判定，
  // 不写入 IWallInfo(不改数据结构)，仅作 helper 内部簿记。
  protected _buildOrder: Map<string, number> = new Map();

  protected _pointPointConnectedQueue: IUpdateItem[] = [];
  protected _pointWallConnectedQueue: IUpdateItem[] = [];

  protected _result: {
    wallId: string;
    type: HandleType;
    points: IShapePoint2d[];
  }[] = [];
  constructor() {}

  get result() {
    return this._result;
  }

  get cacheWallMap() {
    return this._cacheWallMap;
  }

  addCacheWall(wallId: string, wallInfo: IWallInfo, buildIndex?: number) {
    this._cacheWallMap.set(wallId, wallInfo);
    if (typeof buildIndex === 'number') this._buildOrder.set(wallId, buildIndex);
  }

  /** 墙的真实建立顺序(来自 _wallInfos 下标)；未登记的视作最晚建立，与「先绘制切后绘制」的判定约定一致。 */
  orderOf(id: string) {
    return this._buildOrder.get(id) ?? Infinity;
  }

  /**
   * 一条偏移边(自 origin 沿 bodyDir 的射线)被若干偏移弧切割的结果：
   * 取「落在偏移弧实际弧段内、沿 bodyDir 前进(depth ≥ 0)且最深(离 origin 最远)」的界内交点。
   */
  private deepestForwardHitOnArcs(
    origin: IPoint2d,
    bodyDir: IPoint2d,
    offsetArcs: ArcSeg2d[]
  ): { point: IPoint2d; depth: number } | null {
    const ray = new Ray2d(origin, bodyDir);
    let best: { point: IPoint2d; depth: number } | null = null;
    for (const off of offsetArcs) {
      const ips = intersectCommonShape(ray, off.createCircle(), arcThresholdValue);
      if (!ips) continue;
      for (const p of ips) {
        if (sweepOnArc(off, p) === null) continue;
        const depth = projectAlong(p, origin, bodyDir);
        if (depth < -thresholdValue) continue;
        if (!best || depth > best.depth) best = { point: p, depth };
      }
    }
    return best;
  }

  /**
   * 沿边界折线展开【一跳邻边】：从 seeds 各自的端点相连墙里，取通过 accept 的厚 0 邻边。
   * 只走一跳(seeds 为快照)，且不会重复给出已在 seeds 内或已收下的墙。
   */
  private oneHopBoundaryNeighbors(
    seeds: IWallInfo[],
    accept: (neighbor: IWallInfo, seed: IWallInfo) => boolean
  ): IWallInfo[] {
    const seen = new Set(seeds.map((w) => w.id));
    const out: IWallInfo[] = [];
    for (const seed of seeds) {
      for (const n of neighborsOf(seed)) {
        if (seen.has(n.id)) continue;
        const nw = this._cacheWallMap.get(n.id);
        if (!nw || nw.thickness !== 0 || !accept(nw, seed)) continue;
        seen.add(nw.id);
        out.push(nw);
      }
    }
    return out;
  }

  /** 把相连列表里的 id 解析成缓存中的墙信息；取不到的跳过(不会得到缺字段的假 IWallInfo)。 */
  resolve(list: { id: string }[]): IWallInfo[] {
    return list.map((w) => this._cacheWallMap.get(w.id)).filter((w): w is IWallInfo => !!w);
  }

  addToPointPointConnectedQueue(updateItem: IUpdateItem) {
    this._pointPointConnectedQueue.push(updateItem);
  }

  addToPointWallConnectedQueue(updateItem: IUpdateItem) {
    this._pointWallConnectedQueue.push(updateItem);
  }

  startChainCalculate() {
    // 纯端点连接优先处理
    this._pointPointConnectedQueue = sortAndRemoveDuplicates(this._pointPointConnectedQueue);
    while (this._pointPointConnectedQueue.length) {
      const updateItem = this._pointPointConnectedQueue.shift();
      if (!updateItem) break;

      const { wallId, type } = updateItem;

      switch (type) {
        case HandleType.Start:
          this.calExtremeOutline(wallId, type);
          break;
        case HandleType.End:
          this.calExtremeOutline(wallId, type);
          break;
      }
    }

    // 再处理包含墙体连接情况的处理
    this._pointWallConnectedQueue = sortAndRemoveDuplicates(this._pointWallConnectedQueue);
    while (this._pointWallConnectedQueue.length) {
      const updateItem = this._pointWallConnectedQueue.shift();
      if (!updateItem) break;

      const { wallId, type } = updateItem;

      switch (type) {
        case HandleType.Start:
          this.calExtremeOutline(wallId, type);
          break;
        case HandleType.End:
          this.calExtremeOutline(wallId, type);
          break;
      }
    }
  }

  protected calExtremeOutline(wallId: string, type: HandleType) {
    const mainWallInfo = this._cacheWallMap.get(wallId);

    if (!mainWallInfo || mainWallInfo.thickness === 0 || !mainWallInfo.relativeWallInfos) return;

    const mainPointOnWall = onWallOf(mainWallInfo, type);
    const mainPointPointConnected = connectToPointOf(mainWallInfo, type);

    let extremeShapePath: IShapePoint2d[] | null;

    // 与本墙「两个端点都重合」的、另一条有厚度的隔墙(thickness>0)，是与本墙平行、共用两端点的孪生隔墙(镜像/透镜配置)。
    // 这种隔墙不应参与本墙端盖裁剪(否则两条共端点的隔墙会互相把对方的弧/线段串进端盖)。算端盖分支时忽略它，
    // 各隔墙只按其与房间墙的连接独立计算端盖。
    // 注意：必须限定 thickness>0，否则会把同样共用两端点的「房间边界弧(厚 0)」误判为孪生而排除，导致隔墙端盖算错。
    const oppositePoint = farEndOf(mainWallInfo.seg, type);
    const isParallelTwin = (w: { id: string }) => {
      const info = this._cacheWallMap.get(w.id);
      return (
        !!info &&
        info.thickness > 0 &&
        (Point2dUtils.equals(info.seg.start, oppositePoint) || Point2dUtils.equals(info.seg.end, oppositePoint))
      );
    };
    const nonTwinPointPoint = mainPointPointConnected.filter((w) => !isParallelTwin(w));
    // 但若该端点除了孪生隔墙再无其它连接(浮空的弧+直透镜，两端都不接房间墙)，忽略孪生会让端盖退化成自由端平口、
    // 两条隔墙在共享端点处互不对接。此时应把孪生当作普通端点-端点相连，按两墙角平分线 miter 对接。
    const onlyTwinAtEndpoint =
      mainPointOnWall.length === 0 && nonTwinPointPoint.length === 0 && mainPointPointConnected.length > 0;
    const effectivePointPointConnected = onlyTwinAtEndpoint ? mainPointPointConnected : nonTwinPointPoint;

    // 直隔墙端点埋在相连隔墙(厚>0)的带状区内、且穿出其另一侧时(如斜隔墙起点落在横隔墙带内、再向下穿出)，
    // 其可见端盖应是它「穿出该隔墙带的那条边」上的切口，而非与房间墙/远处轮廓求交绕行。
    const bandClipped = this.clipStraightThroughBand(mainWallInfo, type, [
      ...effectivePointPointConnected,
      ...mainPointOnWall
    ]);
    if (bandClipped) {
      this.setLocalWallShape(wallId, type, bandClipped);
      this._result.push({ wallId, type, points: bandClipped });
      return;
    }

    if (mainPointOnWall.length === 0 && effectivePointPointConnected.length === 0) {
      // 自由端点(或仅与平行孪生隔墙共端点)
      extremeShapePath = this.calFreeWallShape(mainWallInfo, type);
    } else if (mainPointOnWall.length === 0) {
      // 处理只有端点端点相连的情况
      extremeShapePath = this.calPointPointConnected(mainWallInfo, type);
    } else if (effectivePointPointConnected.length === 0) {
      // 仅与房间墙相连(平行孪生隔墙已忽略)：按端点落墙处理
      extremeShapePath = this.calPointWallConnected(mainWallInfo, type);
    } else {
      // 处理主墙端点与墙体和其他端点都有连接的情况
      extremeShapePath = this.calMixConnected(mainWallInfo, type);
    }

    // 弧形隔墙在「与相邻弧共点相切」的接头处，通用裁剪会选到对方弧远端的幻影点(端盖绕缠)。这类「相切弧接头」
    // 改用按几何确定性求解：最先建的弧不被切(平口连墙)，其余被更早建立的相切弧按「先绘制切后绘制」切割。
    // 该方法仅对相切弧接头返回结果，其它情形(相交弧、含直隔墙的混合接头等)返回 null，保持通用裁剪不变。
    if (mainWallInfo.center) {
      // 注意：后建墙落在「≥2 条更早相切弧」的公共接头上时【不沿那条平端盖面平切】(曾有 calWallArcJunctionFlatCut，已删除)。
      // 本墙的原始端盖过接头、两个偏移端点必然分居那条端盖面两侧：露在开放侧那个墙角是真实可见的，
      // 而埋在里侧那条偏移边应当与更早墙的轮廓【求交】。可见端盖恒为 cutByEarlier 的布尔差阶梯，见下方各分支。
      // 后建弧墙端点搭接在「厚 0 墙边界」上、且接头还连着≥1 条更早建立的有厚隔墙(多条隔墙搭接在墙边界)：
      // 与直墙版 calStraightMultiCutByEarlier 对称——后画的被先画的沿其轮廓切(用户语义)。按拓扑判定即可接管，
      // 方法内部自带各项 guard(须为接头最后一条、非孪生弦墙、非同向相切续接、切点须在本弧前进段且局部)，
      // 不符则返回 null 交回下方既有逻辑。
      const arcMultiCut = this.calArcMultiCutByEarlier(mainWallInfo, type, [
        ...effectivePointPointConnected,
        ...mainPointOnWall
      ]);
      if (arcMultiCut && arcMultiCut.length) {
        this.setLocalWallShape(wallId, type, arcMultiCut);
        this._result.push({ wallId, type, points: arcMultiCut });
        return;
      }
      const arcCap = calArcJunctionCap(this, mainWallInfo, type);
      if (arcCap && arcCap.length) {
        // 两弧共点接头(arcCap)落在「更早建立的直隔墙 body」上时：保留顶部两弧 miter，把伸进直墙的下部端盖点
        // 裁到直墙近边(先绘制的直墙把两弧交点处的下部盖住)。仅该情形改写，其它接头保持不变。
        const clipped =
          this.clipArcCapByEarlierStraightBody(mainWallInfo, type, arcCap) ??
          this.clipArcCapByEarlierArcBody(mainWallInfo, type, arcCap);
        extremeShapePath = clipped ?? arcCap;
      } else {
        // 弧隔墙端点落在厚 0 边界折线的【某条边 body 中段】上(薄片端头平盖中点等)：戳过边界的偏移沿自身偏移圆退到
        // 局部边界链上的真实出口、端盖经公共角点绕回接头，见 calArcOnBoundaryBodyCut。不符则继续走下面的共点分支。
        const bodyCut = this.calOnBoundaryBodyCut(mainWallInfo, type);
        if (bodyCut && bodyCut.length) {
          this.setLocalWallShape(wallId, type, bodyCut);
          this._result.push({ wallId, type, points: bodyCut });
          return;
        }
        // 弧隔墙端点只与厚 0 边界线共点：默认沿边界线「平切」(calArcBoundaryFlatCut，两偏移都裁到同一条边界线，2 点平口)。
        // 但当平切会把某个端盖点落在「窄口 body 内部」(该点严格落在某条短边界线段中段——即戳进槽口、盖住槽的开口)时，
        // 改走通道口袋切(boundaryPocketCut)：戳入侧偏移被垂直的槽壁裁回，端盖沿边界贴合、不盖住槽口。
        const flat = this.calArcBoundaryFlatCut(mainWallInfo, type);
        const arcConnectedAll = this.resolve([
          ...connectToPointOf(mainWallInfo, type),
          ...onWallOf(mainWallInfo, type)
        ]);
        const arcBoundaries = arcConnectedAll.filter((w) => w.thickness === 0 && w.shape instanceof Line2d);
        // 平切端盖点是否落在某条边界线段的「中段内部」(说明盖住了窄口开口) → 需改走口袋切。
        const flatCoversMouth =
          !!flat &&
          !arcConnectedAll.some((w) => w.thickness > 0) &&
          flat.some((p) =>
            arcBoundaries.some((b) => {
              const t = projT(p, b.seg.start, b.seg.end);
              return Math.abs(signedDistanceToLine(b.seg.start, b.seg.end, p)) < 1 && t > 0.1 && t < 0.9;
            })
          );
        // flatCoversMouth 已判定确为窄口口袋，boundaryPocketCut 内部的「戳出侧」判据在弧远端与边界共线时会退化，故跳过(assumePocket)。
        let pocket = flatCoversMouth ? this.boundaryPocketCut(mainWallInfo, type, arcBoundaries, [], true) : null;
        // 口袋切给出的是「戳入侧偏移∩槽壁」与「另一偏移」两点，直连会斜跨过槽底角、够不到接头角点。
        // 端盖应经接头角点(弧起点 J)连接成 3 点折线(∩槽壁 → 角点 → 另一偏移)，与边界角贴合。J 与两点共线则不插(退化平口)。
        if (pocket && pocket.length === 2) {
          const jp = endOf(mainWallInfo.seg, type);
          const [p0, p1] = pocket;
          const cross = (p1.x - p0.x) * (jp.y - p0.y) - (p1.y - p0.y) * (jp.x - p0.x);
          if (Math.abs(cross) > 1e-3 * Point2dUtils.distanceTo(p0, p1)) pocket = [p0, { x: jp.x, y: jp.y }, p1];
        }
        if (pocket && pocket.length) extremeShapePath = pocket;
        else if (flat && flat.length) extremeShapePath = flat;
      }
    } else {
      // 直隔墙(后建)端点与「更早建立的相切弧」共点、且本直墙确实探入该弧带内：本直墙被该弧(先建)沿其近偏移边裁回端盖
      // (与 calArcJunctionCap 的「相切直墙裁弧」对称，按先绘制切后绘制)。仅对该情形返回结果，其它情形返回 null。
      const straightCap = this.calStraightTangentArcCut(mainWallInfo, type, [
        ...effectivePointPointConnected,
        ...mainPointOnWall
      ]);
      if (straightCap && straightCap.length) extremeShapePath = straightCap;
      else {
        const connected = [...effectivePointPointConnected, ...mainPointOnWall];
        // 注意：直墙接入「≥2 条更早相切弧」的公共接头时【不平切】(同弧主墙，见上方弧分支注释)——
        // 可见端盖恒为下面 calStraightMultiCutByEarlier 给出的布尔差阶梯。
        // 混合多墙接头处第三条及以后建立的直墙：被所有更早的墙轮廓切(与弧主墙的 calArcJunctionCap 第三条+ 对称)。
        const cut = this.calStraightMultiCutByEarlier(mainWallInfo, type, connected);
        if (cut && cut.length) extremeShapePath = cut;
        else {
          // 直墙端点落在厚 0 边界的 body 中段上：戳进边界实心区的偏移退到真实出口、端盖经公共角点绕回接头
          // (见 calOnBoundaryBodyCut)。方法内部只在「出口在一跳邻边上」或「落脚边是弧(圆头薄片端头)」时接管，
          // 落脚在长房间墙/直端头上的常规情形仍走下面 calStraightBoundaryFlatCut 那套。
          const bodyCap = this.calOnBoundaryBodyCut(mainWallInfo, type);
          // 直墙端点落在「厚 0 弧边界 + 厚 0 直边界」共点的接头角上(薄弧带/拱端头)：戳进弧带内的偏移边被裁回边界。
          const arcBcap = bodyCap && bodyCap.length ? null : this.calStraightArcBoundaryFlatCut(mainWallInfo, type);
          if (bodyCap && bodyCap.length) extremeShapePath = bodyCap;
          else if (arcBcap && arcBcap.length) extremeShapePath = arcBcap;
          else {
            // 直墙端点只与厚 0 边界线共点、某偏移边戳过边界：把该偏移边裁回边界(与弧主墙的 calArcBoundaryFlatCut 对称)。
            const bcap = this.calStraightBoundaryFlatCut(
              mainWallInfo,
              type,
              !extremeShapePath || !extremeShapePath.length
            );
            if (bcap && bcap.length) extremeShapePath = bcap;
          }
        }
      }
    }

    if (!extremeShapePath || extremeShapePath.length === 0) return;

    this.setLocalWallShape(wallId, type, extremeShapePath);
    this._result.push({ wallId, type, points: extremeShapePath });
  }

  // 直隔墙端点埋在相连隔墙(厚>0)的带状区内、并从另一侧穿出时，端盖取它穿出该带的「最远穿出边」上的切口：
  // 先沿 body 方向找两条偏移边与相连隔墙轮廓的最远(>本墙厚)穿出点，确定穿出边，再用该边(直线/弧的延长形态)
  // 切两条偏移边得到端盖两点。仅在确有「穿透」时生效(最远穿出 > 本墙厚)，否则返回 null 走常规逻辑，
  // 不影响普通端点-墙/端点-端点/孪生隔墙等情形。
  private clipStraightThroughBand(
    mainWallInfo: IWallInfo,
    type: HandleType,
    connectedInfos: { id: string }[]
  ): IShapePoint2d[] | null {
    if (mainWallInfo.center) return null; // 仅处理直隔墙
    // 交汇处两条隔墙几何对称，无法纯几何判断"谁切谁"：按建立先后定优先级——本墙只被「更早建立」的隔墙切割，
    // 让位给先有的隔墙(用户语义：后建的第七条被先建的第六条切)。避免两条隔墙在交汇点互相切割。
    // 建立先后用真实建立顺序(_buildOrder，来自 _wallInfos 下标)判定，不能用缓存插入顺序：全量计算时房间墙先入
    // 缓存会打乱缓存顺序，把先建墙误判为「后建」而错切它。与 calMixConnected / calArcJunctionCap 一致。
    const mainOrder = this.orderOf(mainWallInfo.id);
    const sepWalls = this.resolve(connectedInfos).filter(
      (w) => w.thickness > 0 && w.id !== mainWallInfo.id && this.orderOf(w.id) < mainOrder
    );
    // 只有「3 条及以上」隔墙交汇时，本墙(后建的第三条)才被先建的两条形成的带切割；
    // 只有一条更早的相连隔墙时，是普通两墙成角相接，应走角平分线 miter，不在此切割。
    if (sepWalls.length < 2) return null;

    const seg = mainWallInfo.seg;
    const startPt = endOf(seg, type);
    const otherPt = farEndOf(seg, type);
    const bodyDir = Point2dUtils.getUnitDirection(startPt, otherPt);
    const leftOrigin = endOf(mainWallInfo.leftSeg, type);
    const rightOrigin = endOf(mainWallInfo.rightSeg, type);

    // 本墙端点埋在多条相连隔墙带的「并集」内，应切在它穿出并集(离开所有重叠带)的地方。
    // 用相连隔墙的「原始偏移边」(leftSeg/rightSeg)作带边(不依赖端盖计算，始终干净)。
    // 并集出口 = 各条带各自「最近穿出距离」里的最大值——穿出所有带之后才算离开并集；
    // 取该最大值对应的那条带边作为穿出边。
    //   · 不能取全局最近：那只穿出了第一条带，仍埋在其它带里；
    //   · 不能取全局最远：相连隔墙里若有向远处大幅延伸的带(如大圆弧)，会跑到很远把本墙整条切没。
    const rays = [new Ray2d(leftOrigin, bodyDir), new Ray2d(rightOrigin, bodyDir)];
    let unionExit = -1;
    let exitSeg: IShapeSeg2d | null = null;
    // 本墙「确实穿过」的先建墙。端盖只对这些墙做切割：透镜配置里向另一侧鼓的孪生弧只在共享角点附近被掠过、
    // 穿出距离不足本墙厚度 → 不入选 → 不会把弧点混进端盖。判据见下方两步过滤。
    const passedWalls: IWallInfo[] = [];
    for (const w of sepWalls) {
      // 本墙两条偏移边各自穿出这条带的最近界内交点距离(及所穿出的边、该交点)。
      const perRay = rays.map((ray) => {
        let nearest = Infinity;
        let seg: IShapeSeg2d | null = null;
        let point: IPoint2d | null = null;
        for (const e of [w.leftSeg, w.rightSeg]) {
          const ips = intersectCommonShape(ray, e);
          if (!ips) continue;
          for (const p of ips) {
            const d = Point2dUtils.distanceTo(ray.origin, p);
            if (d > thresholdValue && d < nearest) {
              nearest = d;
              seg = e;
              point = p;
            }
          }
        }
        return { nearest, seg, point };
      });
      // 至少有一条偏移边穿出该带(界内交点)才算本墙「穿过」它：
      //   · 直墙带：本墙在角点处某条偏移边的穿出点可能略越过该墙端点(正常斜接 overshoot)，只要另一条边界内穿出即算穿过；
      //   · 弧墙带：向另一侧鼓的孪生弧不在本墙前进方向的角度跨度内，两条边都无界内交点 → 不入选，弧点不会混进端盖。
      const valid = perRay.filter((r) => r.seg);
      if (!valid.length) continue;
      // 这条带最深的「界内穿出」距离。只有 ≥ 本墙厚度才算「真正穿过」这条带：
      // 透镜里向另一侧鼓的孪生弧，本墙只在共享角点附近掠过其一条边(穿出距离 < 厚度) → 不入选，弧点不会混进端盖；
      // 真正穿过的墙(直墙带 / 向本墙方向鼓的弧)穿出距离 ≥ 厚度(垂直穿越恰为厚度，斜穿更大)。
      const later = valid.reduce((a, b) => (a.nearest >= b.nearest ? a : b));
      // 「穿出距离 ≥ 一个带宽」这道门槛的前提是偏移边【从带外进、从另一侧出】。接头处三条以上墙成扇形、本墙夹在
      // 两条更早墙之间时，本墙的某条偏移端点一开始就【埋在】那条更早墙的实心带里，剩下的穿出距离天然小于带宽
      // (实测 40.8mm < 70mm)，这条墙于是被漏掉：passedWalls 只剩另一条 → 走单边平切 → 沿那条墙侧边线的
      // 【延长线】把两条偏移边一刀切平，切点落进被漏掉的这条墙体内部，两条墙轮廓交叉。
      // 补一条等价判据：偏移端点严格埋在带内、且这条偏移边【跨过了该墙中线】(出口与端点分居中线两侧)。
      // 从带外进、从另一侧出必然跨中线，两者说的是同一件事「真正穿过这条带」，只是起点在带内时距离量不足。
      // 只判「埋着」不够，必须要求跨中线：透镜里向另一侧鼓的孪生弧，本墙偏移端点也会落进弧带靠边处，但它是从
      // 同一侧擦出去的(不跨中线)；共线反向两墙拼成的贯通带同理，靠接头那条只被擦到 11mm —— 这两类正是本门槛要挡的。
      // 必须用严格版 isStrictlyInsideBand：方形端帽外扩会把「落在对方端盖面身后」也算成埋着。
      const centerShape = w.shape;
      const crossesCenterline =
        (centerShape instanceof Line2d || centerShape instanceof Circle2d) &&
        rays.some((ray, index) => {
          const { point } = perRay[index];
          if (!point || !isStrictlyInsideBand(ray.origin, w.seg, w.thickness / 2)) return false;
          return signedSideOfCenterline(ray.origin, centerShape) * signedSideOfCenterline(point, centerShape) < 0;
        });
      if (!crossesCenterline && later.nearest < mainWallInfo.thickness - thresholdValue) continue;
      passedWalls.push(w);
      if (later.seg && later.nearest > unionExit) {
        unionExit = later.nearest;
        exitSeg = later.seg;
      }
    }
    if (!exitSeg || !passedWalls.length) return null;

    // 必须「确有穿透」才走切口：穿出并集的距离要大于本墙厚度。
    // 否则只是本墙端点掠过相连隔墙的角(如斜墙从横墙+竖墙的 L 外角发散出去，仅穿出十几毫米)，
    // 这种应走常规端点处理(角平分线/绕角的反 L 轮廓)，而非把端盖一刀平切。
    // 注意：此门槛是纯几何判断、与计算时主墙体集合(绘制增量 vs 打开全量)无关，保证两条路径结果一致。
    if (unionExit < mainWallInfo.thickness + thresholdValue) return null;

    let cap: IShapePoint2d[] | null;
    // 只穿过一条先建墙：端盖是本墙穿出该墙的「单边平切」——两条偏移边沿 body 方向分别切到穿出边(直线/弧)上的两点，
    // 不绕该墙的端盖/拐角(避免戳出)。
    if (passedWalls.length === 1) {
      const extended = extendSeg(exitSeg);
      const clip = (origin: IPoint2d): IPoint2d | null => {
        const mainLine = new Line2d({
          points: { p1: origin, p2: { x: origin.x + bodyDir.x, y: origin.y + bodyDir.y } }
        });
        const ips = intersectCommonShape(mainLine, extended);
        if (!ips || !ips.length) return null;
        const forward = ips.filter((p) => projectAlong(p, origin, bodyDir) >= -thresholdValue);
        return getNearestPoint(forward.length ? forward : ips, origin);
      };
      const lp = clip(leftOrigin);
      const rp = clip(rightOrigin);
      // 端盖点顺序统一为 [左, 右]，与 startShape/endShape 的约定一致(见 getWallDrawShape：
      // drawShape = [...reverseShape(startShape), ...endShape]，两端封口都存 [左, 右]才不会连成蝴蝶结)。
      // 之前 end 端返回 [右, 左]，会让 drawShape 在端封口处对角交叉、轮廓自交。
      cap = lp && rp ? [lp, rp] : null;
    } else {
      // 穿过多条先建墙(>2 条隔墙交汇)：端盖由这些墙「共同切割」——
      // 用 PathFinder 沿各墙(弧/直)轮廓的并集边界，从本墙左偏移边的入口走到右偏移边的入口，得到复合端盖。
      cap = this.calPointWallIntersection(passedWalls, mainWallInfo, type, true);
    }
    if (!cap) return null;

    // 端盖戳出本墙端点所在的房间/结构墙(厚 0)边线时——相连隔墙的带在接头处正好到头、裁不到本墙某条偏移边，
    // 该偏移边会越过房间墙边线伸到室外(单边平切尤其如此)。此时带上这条房间墙边线作为边界重算，把端盖裁回房间内。
    // 室内侧 = 本墙另一端点 otherPt 所在侧(本墙从房间墙伸入室内)。仅在确有戳出时才重算，不影响不戳出的端盖。
    const boundaryWalls = this.resolve(connectedInfos).filter(
      (w) => w.thickness === 0 && w.id !== mainWallInfo.id && w.shape instanceof Line2d
    );
    // 本墙端点落在某条厚 0 边界 body 上(端点埋在边界折线里)时，某条偏移边会从该边界的【相邻一条边】穿出
    // (如落在薄条端头平盖上、偏移戳进薄条、从相邻长边穿出)。只用直接相连的那条边界裁不到该偏移边——穿出边不在集合里。
    // 补上这些边界的「端点相连一跳邻边(厚 0 直线)」一并作边界重算，使戳进边界的偏移边被真正的穿出边裁回。
    boundaryWalls.push(...this.oneHopBoundaryNeighbors(boundaryWalls, (nw) => nw.shape instanceof Line2d));
    // 戳出量阈值：端盖在连接角处略越过边线属正常斜接 overshoot(几毫米)，不重算；
    // 只有偏移边明显越过房间墙边线(戳出量 > 壁厚的一定比例)才判定为真戳出、需裁回。
    const protrudeTol = mainWallInfo.thickness * 0.2;
    // 「戳出」必须按边界【线段】判，不能用它所在的无限直线：接头是薄片/薄柱的尖端角点时，边界线段到接头就到头了，
    // 端盖点落在尖端【身后】(沿边界方向投影超出线段)时，它所在的无限直线仍会把这个点算成"在外侧"——本例正确的单边平切
    // 两点里有一点在尖端身后 11mm，被薄片两条长边的延长线双双误判为戳出 16/116mm，端盖被推去和边界重算、
    // 沿无限延长的房间顶墙求到 6000mm 外的交点，甩出长尖刺。加投影闸后这类身后点不再触发重算。
    // 不留容差：越过边界线段端点(角点)之外那一侧本就没有这道边界，该由角点另一条边界墙负责——
    // 它们(含一跳邻边)都已在 boundaryWalls 里，真戳出房间角的端盖仍会被其中一条判到。
    const withinSegSpan = (p: IPoint2d, bw: IWallInfo) => {
      const a = bw.seg.start;
      const b = bw.seg.end;
      const len = Point2dUtils.distanceTo(a, b);
      if (len <= thresholdValue) return false;
      const t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / len;
      return t >= 0 && t <= len;
    };
    // cap 在下面还会被重新赋值，闭包里直接读它会丢掉上面的判空收窄，这里先取一份已收窄的引用
    const capPoints = cap;
    const protrudes = boundaryWalls.some((bw) => {
      const sideRef = signedSideOfCenterline(otherPt, bw.shape as Line2d);
      return capPoints.some((p) => {
        const sideP = signedSideOfCenterline(p, bw.shape as Line2d);
        return sideP * sideRef < 0 && Math.abs(sideP) > protrudeTol && withinSegSpan(p, bw);
      });
    });
    if (protrudes) {
      const clipped = this.calPointWallIntersection([...passedWalls, ...boundaryWalls], mainWallInfo, type, true);
      if (clipped && clipped.length) cap = clipped;
    }
    return cap;
  }

  // 本墙两条偏移边沿 body 前向是否确实穿出这条边界的【界内】——与 boundaryPocketCut 里的 arcExitForward /
  // lineExitForward 同一判据。用来筛「一跳邻边」：接头那条边界的另一头可能挂着几千毫米外的房间墙，
  // 它在本墙身后、偏移边永远到不了，纳入只会让 PathFinder 选到远端假点。仅供直主墙使用(弧主墙的 bodyDir 是弦方向)。
  private boundaryReachableForward(mainWallInfo: IWallInfo, type: HandleType, w: IWallInfo): boolean {
    const junction = endOf(mainWallInfo.seg, type);
    const otherPt = farEndOf(mainWallInfo.seg, type);
    const bodyDir = Point2dUtils.getUnitDirection(junction, otherPt);
    const origins = [endOf(mainWallInfo.leftSeg, type), endOf(mainWallInfo.rightSeg, type)];
    for (const o of origins) {
      const ips = intersectCommonShape(new Ray2d(o, bodyDir), w.seg);
      if (!ips) continue;
      for (const p of ips) {
        if (projectAlong(p, o, bodyDir) >= thresholdValue) return true;
      }
    }
    return false;
  }

  private getShapeSegInfos(wallInfos: IWallInfo[]) {
    const handleWallShapeSegs: { seg: IShapeSeg2d; wallId: string }[] = [];
    wallInfos.forEach((wallInfo) => {
      const drawShape = wallInfo.drawShape;
      if (drawShape.length === 2) {
        if (wallInfo.skipCalculate) return;
        const [start, end] = drawShape;
        const shapeSeg = getShapeSeg({ start, end, center: start.center, clockwise: start.clockwise });
        handleWallShapeSegs.push({ seg: shapeSeg, wallId: wallInfo.id });
        return;
      }

      // 弧形墙体处理
      if (wallInfo.seg instanceof ArcSeg2d) {
        const startShape = wallInfo.startShape;
        const endShape = wallInfo.endShape;
        for (let index = startShape.length - 1; index > 0; index--) {
          handleWallShapeSegs.push({
            seg: new LineSeg2d(startShape[index], startShape[index - 1]),
            wallId: wallInfo.id
          });
        }

        // 左弧
        const leftSeg = wallInfo.leftSeg as ArcSeg2d;

        handleWallShapeSegs.push({
          seg: new ArcSeg2d({
            start: startShape[0],
            end: endShape[0],
            center: leftSeg.center,
            clockwise: leftSeg.clockwise
          }),
          wallId: wallInfo.id
        });

        for (let index = 0; index < endShape.length - 1; index++) {
          handleWallShapeSegs.push({
            seg: new LineSeg2d(endShape[index], endShape[index + 1]),
            wallId: wallInfo.id
          });
        }

        // 右弧
        const rightSeg = wallInfo.rightSeg as ArcSeg2d;
        handleWallShapeSegs.push({
          seg: new ArcSeg2d({
            start: endShape[endShape.length - 1],
            end: startShape[startShape.length - 1],
            center: rightSeg.center,
            clockwise: !rightSeg.clockwise
          }),
          wallId: wallInfo.id
        });

        return;
      }

      // 直墙的端盖里也可能有弧段：端点搭在厚 0 弧边界(薄弧带)上时端盖会沿那条弧绕过去，drawShape 对应点上带着
      // center/clockwise(约定同 2 点分支与 reverseShape：弧信息描述「从该点出发」的那一段)。这里照 getShapeSeg 还原成
      // ArcSeg2d，不能一律 LineSeg2d 拍成弦——弦与真弧只差零点几毫米，却会与同一条弧边界形成两条几乎重合、
      // 却不互为反向边的边，PathFinder 的分裂与反向边抵消随之错乱。
      for (let index = 0; index < drawShape.length; index++) {
        const segStart = drawShape[index];
        const segEnd = drawShape[(index + 1) % drawShape.length];
        handleWallShapeSegs.push({
          seg: getShapeSeg({ start: segStart, end: segEnd, center: segStart.center, clockwise: segStart.clockwise }),
          wallId: wallInfo.id
        });
      }
    });

    return handleWallShapeSegs;
  }

  // 自由墙体端点轮廓
  private calFreeWallShape(mainWallInfo: IWallInfo, type: HandleType): IPoint2d[] {
    return [endOf(mainWallInfo.leftSeg, type), endOf(mainWallInfo.rightSeg, type)];
  }

  // 获取与主墙体端点端点连接的墙体信息
  private getPointPointConnectedWallInfos(mainWallInfo: IWallInfo, type: HandleType) {
    const rel = mainWallInfo.relativeWallInfos;
    // 与本文件其他取缓存墙的地方一致：取不到的跳过。展开 undefined 不会报错，只会得到缺字段的假 IWallInfo
    const infos = connectToPointOf(mainWallInfo, type).flatMap((item) => {
      const wallInfo = this._cacheWallMap.get(item.id);
      if (!wallInfo) return [];

      return [
        {
          ...wallInfo,
          handleType: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          id: item.id
        }
      ];
    });
    const order =
      (type === HandleType.Start ? rel?.mainStartConnectToPointOrder : rel?.mainEndConnectToPointOrder) || 0;

    return { infos, order };
  }

  // 端点端点相连
  protected calPointPointConnected(mainWallInfo: IWallInfo, type: HandleType): IShapePoint2d[] | null {
    const { infos: pointPointConnectedInfos, order } = this.getPointPointConnectedWallInfos(mainWallInfo, type);

    // ------------------没有端点端点连接的墙体------------------
    if (pointPointConnectedInfos.length === 0) return [];

    // 弧形隔墙端点落在角点、与某相连墙相切：角点处常连着两面墙(侧墙 + 底墙)，底墙线正好穿过偏移端点，
    // 一般求交会把外点算成「外弧∩底墙线」(戳出墙外的端点) 而非「外弧∩侧墙」的跨内交点，导致外边不到墙、墙厚被压窄。
    // 这里只取与隔墙相切的那面侧墙交给 calPointWallIntersection，按 L 形端盖算出外弧与侧墙的真实交点(保住墙厚)。
    // 仅限厚 0 的房间/结构墙：弧端点是「贴在这面墙上」需按墙裁出 L 形端盖。若相切的是厚>0 的隔墙、且只在此端点端点相连
    // (是弧的平滑延续，如直隔墙与弧在切点处顺接)，不应按「贴墙裁剪」处理——那会把外偏移弧算到对方隔墙远端的幻影交点上、
    // 起点轮廓错误。这类相切延续交由下方通用逻辑按角平分线(平行分支)对接。
    if (mainWallInfo.center) {
      const tangentWalls = pointPointConnectedInfos.filter(
        (w) => w && w.thickness === 0 && isTangentShape(mainWallInfo.shape, w.shape)
      );
      if (tangentWalls.length) {
        const res = this.calPointWallIntersection(tangentWalls, mainWallInfo, type, true);
        if (res && res.length) return res;
      }
    }

    let mainLeftPoint;
    let mainRightPoint;

    const prevWallInfos = pointPointConnectedInfos.slice(0, order); // 在该墙体之前添加的墙体
    const laterWallInfos = pointPointConnectedInfos.slice(order); // 在该墙体之后添加的墙体

    if (order === 0) {
      // --- 前两条连接墙体需要特殊处理 ---
      // order 为 0 时 laterWallInfos 就是全部连接墙，上面已排除空数组，这里必能取到
      const firstLaterWallInfo = laterWallInfos.shift();
      if (firstLaterWallInfo) prevWallInfos.push(firstLaterWallInfo);
    }

    // --- 两墙端点相连 ---
    if (prevWallInfos.length === 1) {
      const handleWallInfo = prevWallInfos[0];

      const isRightAngle = isRightAngleShape(mainWallInfo.shape, handleWallInfo.shape);
      const isParallel = isTangentShape(mainWallInfo.shape, handleWallInfo.shape);
      if (isRightAngle) {
        let _mainId = mainWallInfo.id;
        let _connectedId = handleWallInfo.id;
        if (order === 1) {
          // 如果当前线段为第二条，主副线段需要交换
          [_mainId, _connectedId] = [_connectedId, _mainId];
        }
        const result = this.calLeftRightIntersectionWithRightAngle(_mainId, _connectedId);
        if (result) {
          mainLeftPoint = order === 0 ? result.mainLeft : result.otherLeft;
          mainRightPoint = order === 0 ? result.mainRight : result.otherRight;
        }
      } else if (isParallel) {
        // 平行线，需要计算垂线交点
        const result = this.calLeftRightIntersectionWithParallel(mainWallInfo.id, handleWallInfo.id, type);
        if (result) {
          mainLeftPoint = result.mainLeftPoint;
          mainRightPoint = result.mainRightPoint;
        }
      } else {
        const mainConnectedPoint = endOf(mainWallInfo.seg, type); // todo: 主要用于判断最近交点，待优化
        // 计算交点
        const result = this.calLeftRightIntersection(mainWallInfo, handleWallInfo, mainConnectedPoint, type);

        mainLeftPoint = result.mainLeft;
        mainRightPoint = result.mainRight;

        // 仅一侧求到斜接点(直+弧的一侧偏移边够不到对方)：端盖经接头点连成 3 点折线 [斜接顶点, 接头点, 另一侧端点]。
        // 若只共享斜接顶点、两侧各收 2 点，接头角会留三角缺口(两墙都盖不到接头中线点)。经接头点后两墙沿
        // 「斜接顶点→接头」公共缝对接、填满接头角，才是正确的角平分线斜拼。
        if (mainLeftPoint && mainRightPoint && result.leftHit !== result.rightHit) {
          return [mainLeftPoint, { x: mainConnectedPoint.x, y: mainConnectedPoint.y }, mainRightPoint];
        }
      }

      return mainLeftPoint && mainRightPoint ? [mainLeftPoint, mainRightPoint] : null;
    }

    // --- 弧形主墙与「共用另一端点的孪生直隔墙」相连(弧是从该直墙鼓出的穹顶/弧，直墙为弦，如"盒子+穹顶"顶边) ---
    // 弧从直墙面上鼓出，端盖应在直墙朝弧一侧的近边上「平切」(内外偏移各自 ∩ 近边线，取近角点的交点)，
    // 两切点都落在近边上 → 弧不插入该直墙、也不被拉到单侧。此处即用户语义的"平切"。
    if (
      mainWallInfo.center &&
      mainWallInfo.leftShape instanceof Circle2d &&
      mainWallInfo.rightShape instanceof Circle2d
    ) {
      const oppositePoint = farEndOf(mainWallInfo.seg, type);
      const twin = prevWallInfos.find(
        (w) =>
          !!w &&
          w.thickness > 0 &&
          !w.center &&
          w.shape instanceof Line2d &&
          (Point2dUtils.equals(w.seg.start, oppositePoint) || Point2dUtils.equals(w.seg.end, oppositePoint))
      );
      if (twin && twin.shape instanceof Line2d) {
        // 近边 = 直墙朝弧本体(弧顶)那一侧的偏移边。
        const apex = (mainWallInfo.seg as ArcSeg2d).getMidPoint();
        const nearLine = extendSeg(nearOffsetSegOf(twin, twin.shape, apex));
        const corner = endOf(mainWallInfo.seg, type);
        const cutOnNear = (circle: Circle2d): IShapePoint2d | null =>
          getNearestPoint(intersectCommonShape(circle, nearLine) || [], corner);
        const leftCut = cutOnNear(mainWallInfo.leftShape);
        const rightCut = cutOnNear(mainWallInfo.rightShape);
        if (leftCut && rightCut) return [leftCut, rightCut];
      }
    }

    // --- 弧形主墙在角点与多面墙相连、其中一面是与弧相切的隔墙(厚>0)、且无孪生直墙 ---
    // 弧是该相切墙的平滑延续(相切续接，如一条直墙在切点分出的弧、或多条相切隔墙共点)。端盖按平行分支顺接该相切墙得平口
    // (本弧内外偏移端点)，而非被多墙求交把某条偏移斜接到远处幻影点。多条相切时优先取更早建立的那条(先绘制的续接基准)。
    if (mainWallInfo.center) {
      const tangentSeps = prevWallInfos.filter(
        (w) => !!w && w.thickness > 0 && !w.center && isTangentShape(mainWallInfo.shape, w.shape)
      );
      const tangentSep = tangentSeps.reduce<IWallInfo | undefined>(
        (a, b) => (!a || this.orderOf(b.id) < this.orderOf(a.id) ? b : a),
        undefined
      );
      if (tangentSep) {
        const result = this.calLeftRightIntersectionWithParallel(mainWallInfo.id, tangentSep.id, type);
        if (result && result.mainLeftPoint && result.mainRightPoint) {
          return [result.mainLeftPoint, result.mainRightPoint];
        }
      }
    }

    // --- 多条墙体端点相连 ---
    // 弧形主墙在角点与多面墙相连时，用「本体同侧 + 跨角内」判据选交点(pointOnWall=true)，
    // 而非直墙斜接的 dot 判据——后者在 Start/End 判据相反，会让弧墙两端选到关于连接点对称的不同交点(一端跨内、一端跨外)。
    return this.calPointWallIntersection(prevWallInfos, mainWallInfo, type, !!mainWallInfo.center);
  }

  // 端点与墙体相连
  protected calPointWallConnected(mainWallInfo: IWallInfo, type: HandleType): IShapePoint2d[] | null {
    const handleWallInfos = onWallOf(mainWallInfo, type);

    // 直隔墙端点搭在「更早建立的弧隔墙 body」上(端点埋在弧带内)：本直墙(后建)被该弧(先建)裁切——去掉压在弧带里的那截，
    // 端盖退到本墙两条偏移边各自「沿 body 方向穿出弧带」的那条弧边上。通用 calPointWallIntersection 在这种浅角搭接下会沿
    // 弧轮廓 PathFinder 绕行、选到弧远端交点把整条弧缠进端盖并自交，故在此专门处理。
    const cutByArc = this.calStraightOnArcBodyCutByArc(mainWallInfo, type, handleWallInfos);
    if (cutByArc) return cutByArc;

    // 端点落在墙体内部(T 型)：两侧拼到相邻墙朝向主墙本体那条边，避免嵌入墙体
    return this.calPointWallIntersection(handleWallInfos, mainWallInfo, type, true);
  }

  // 本墙端点落在厚 0 边界 body 上时的「局部边界链」：落脚边界的端点相连一跳邻边(厚 0)，按公共角点 ≤6×墙厚筛。
  private localBoundaryNeighbors(mainWallInfo: IWallInfo, type: HandleType, infos: IWallInfo[]): IWallInfo[] {
    const junction = endOf(mainWallInfo.seg, type);
    const reach = mainWallInfo.thickness * 6;
    return this.oneHopBoundaryNeighbors(
      infos.filter((w) => w.thickness === 0),
      (nw, seed) => sharedCornerWithin(seed.seg, nw.seg, junction, reach)
    );
  }

  // 直隔墙端点搭在单条「更早建立」的弧隔墙 body 上、且端点埋在弧带内：本直墙被该弧裁切。
  // 两条偏移边各自从接头端点沿 body 方向(射线)，取「落在弧实际弧段内、前向(depth≥0)、最深(离端点最远)」的与弧偏移圆交点——
  // 即本墙穿出弧带的那条边上的切口；压在弧带里的那截被去掉。两侧都求到才采用，否则交回通用逻辑。
  private calStraightOnArcBodyCutByArc(
    mainWallInfo: IWallInfo,
    type: HandleType,
    handleWallInfos: IWallInfo[]
  ): IShapePoint2d[] | null {
    if (mainWallInfo.center) return null; // 仅直隔墙主墙
    if (handleWallInfos.length !== 1) return null; // 仅搭在单一墙上的简单情形
    const arc = handleWallInfos[0];
    if (!(arc.shape instanceof Circle2d) || !(arc.seg instanceof ArcSeg2d)) return null; // on-wall 须为弧
    const leftOff = arc.leftSeg;
    const rightOff = arc.rightSeg;
    if (!(leftOff instanceof ArcSeg2d) || !(rightOff instanceof ArcSeg2d)) return null;
    // 先绘制的弧才裁后绘制的直墙(先建切后建)。
    if (this.orderOf(arc.id) >= this.orderOf(mainWallInfo.id)) return null;

    const junction = endOf(mainWallInfo.seg, type);
    const otherPt = farEndOf(mainWallInfo.seg, type);
    // 接头端点须埋在弧带内(落在弧 body 上必然如此，保险检查)。
    // 取 seg.center 而非可选的 arc.center：上面已把 arc.seg 收窄为 ArcSeg2d，其圆心必存在且与两条偏移弧同心
    const jr = Point2dUtils.distanceTo(junction, arc.center ?? arc.seg.center);
    const innerR = Math.min(leftOff.radius, rightOff.radius);
    const outerR = Math.max(leftOff.radius, rightOff.radius);
    if (jr < innerR - arcThresholdValue || jr > outerR + arcThresholdValue) return null;

    const bodyDir = Point2dUtils.getUnitDirection(junction, otherPt);
    const leftOrigin = endOf(mainWallInfo.leftSeg, type);
    const rightOrigin = endOf(mainWallInfo.rightSeg, type);
    const deepestExit = (origin: IPoint2d) =>
      this.deepestForwardHitOnArcs(origin, bodyDir, [leftOff, rightOff])?.point ?? null;
    const l = deepestExit(leftOrigin);
    const r = deepestExit(rightOrigin);
    if (!l || !r) return null;
    // 护栏：切口不应离接头过远(≤8×墙厚)。近乎相切时穿出距离会失控，交回通用逻辑而非切出夸张的斜口。
    const limit = mainWallInfo.thickness * 8;
    if (Point2dUtils.distanceTo(l, junction) > limit || Point2dUtils.distanceTo(r, junction) > limit) return null;
    return [l, r];
  }

  // 直隔墙(后建)端点与「更早建立的相切弧隔墙」共点、且本直墙确实探入该弧带内(探入深度>本墙厚)——两墙带在接头处重叠。
  // 此时本直墙被该弧(先建)沿其近偏移边裁回端盖：两条偏移边各自沿 body 方向被弧的两条偏移圆切到的「最深(离接头最远)」
  // 界内交点，得到 2 点弦端盖(与 clipStraightThroughBand 被弧带切割时同为弦端盖，保持一致)。
  // 探入不足(相切平滑延续，如 arcStraightTangentSep：偏移边与弧近乎相切、只在接头处相掠) → 返回 null 走通用平口端盖，
  // 不影响其它情形。判据(相切 + 探入>墙厚)与 calArcJunctionCap 的「相切直墙裁弧」对称。
  private calStraightTangentArcCut(
    mainWallInfo: IWallInfo,
    type: HandleType,
    connectedInfos: { id: string }[]
  ): IShapePoint2d[] | null {
    if (mainWallInfo.center) return null; // 仅处理直隔墙
    const mainOrder = this.orderOf(mainWallInfo.id);
    // 接头处相连的、更早建立且与本直墙相切的弧隔墙(厚>0)。
    const earlierArcs = this.resolve(connectedInfos).filter(
      (w) =>
        w.thickness > 0 &&
        w.id !== mainWallInfo.id &&
        w.shape instanceof Circle2d &&
        this.orderOf(w.id) < mainOrder &&
        isTangentShape(mainWallInfo.shape, w.shape)
    );
    if (!earlierArcs.length) return null;

    const junction = endOf(mainWallInfo.seg, type);
    const otherPt = farEndOf(mainWallInfo.seg, type);
    const bodyDir = Point2dUtils.getUnitDirection(junction, otherPt);
    const leftOrigin = endOf(mainWallInfo.leftSeg, type);
    const rightOrigin = endOf(mainWallInfo.rightSeg, type);

    // 本直墙一条偏移边被这些弧带切割：与各弧的两条偏移圆求交，取沿 body 前向最深的界内交点。
    const earlierOffsetArcs = earlierArcs
      .flatMap((arc) => [arc.leftSeg, arc.rightSeg])
      .filter((off): off is ArcSeg2d => off instanceof ArcSeg2d);
    const deepestCut = (origin: IPoint2d) => this.deepestForwardHitOnArcs(origin, bodyDir, earlierOffsetArcs);

    const leftCut = deepestCut(leftOrigin);
    const rightCut = deepestCut(rightOrigin);
    // 确有探入(最深>本墙厚)才裁；否则相切平滑延续 → 交回通用逻辑走平口端盖。
    if (Math.max(leftCut?.depth ?? 0, rightCut?.depth ?? 0) < mainWallInfo.thickness + thresholdValue) return null;

    return [leftCut ? leftCut.point : leftOrigin, rightCut ? rightCut.point : rightOrigin];
  }

  // 弧隔墙端点只与「厚 0 边界线」(房间/结构线、构成薄条闭环的边线等)端点-端点共点、接头处再无其它有厚隔墙时：
  // 通用求交会把弧的外偏移端点留在自身端点处(戳进边界内)、内偏移端点裁到某条边界线上，形成经过接头中线点的凹口端盖。
  // 用户语义：该接头「直接被边界线平切」。做法：把弧的内、外两条偏移圆都裁到同一条边界线上，得到齐平边界的平口端盖。
  // 接头常是薄条的一个直角角点(两条边界线在此相交)——选「裁得最紧(两切点离接头最近)」的那条边界线，端盖最贴合、不外扩。
  private calArcBoundaryFlatCut(mainWallInfo: IWallInfo, type: HandleType): IShapePoint2d[] | null {
    if (!(mainWallInfo.shape instanceof Circle2d) || !(mainWallInfo.seg instanceof ArcSeg2d)) return null;
    if (!(mainWallInfo.leftShape instanceof Circle2d) || !(mainWallInfo.rightShape instanceof Circle2d)) return null;
    const infos = this.resolve(connectToPointOf(mainWallInfo, type));
    if (!infos.length) return null;
    // 接头处必须全是厚 0 边界墙(有厚>0 隔墙则是隔墙接头，另有处理)。
    if (infos.some((w) => w.thickness > 0)) return null;
    // 弧与某条边界墙相切(如弧端点落在房间角点、弧相切于侧墙)：应走 L 形贴墙端盖(calPointPointConnected 的相切分支)，
    // 不在此平切。本方法只处理弧与边界线成角相接(非相切)、外偏移端点戳进边界成凹口的情形。
    if (infos.some((w) => isTangentShape(mainWallInfo.shape, w.shape))) return null;

    const junction = endOf(mainWallInfo.seg, type);
    const outerIsLeft = mainWallInfo.leftShape.radius >= mainWallInfo.rightShape.radius;
    const outerShape = outerIsLeft ? mainWallInfo.leftShape : mainWallInfo.rightShape;
    const innerShape = outerIsLeft ? mainWallInfo.rightShape : mainWallInfo.leftShape;

    const corner = this.calArcBoundaryCornerCut(mainWallInfo, type, infos, outerIsLeft);
    if (corner) return corner;

    // 平切点必须落在这条边界【线段】范围内，不能用它所在的无限直线：接头是薄片/薄条的【尖端角点】时，
    // 落脚的那条长边到接头 J 就到头了，J 之外那一侧根本没有这道边界。用无限直线会把「没戳出、在开放侧」的那条偏移
    // 沿延长线一起拉过去 —— 实测弧从 200 宽薄片尖端斜出时，内偏移被向接头【身后】延长 74mm(该点在落脚边线段外
    // t=1.0175)，凭空多出一截贴着延长线的墙体、还横切过薄片的尖端角。
    // 越界的那一侧不裁、保持自身偏移端点，端盖再经接头角点折过去(与直墙版 calStraightBoundaryFlatCut 的
    // extendOffsetTo 同一条判据)。两侧都落在线段内时点位与原来完全一致，且 J 也在这条边界线上 → 三点共线、
    // 仍退化成 2 点平口。
    const outerEnd = endOf(outerIsLeft ? mainWallInfo.leftSeg : mainWallInfo.rightSeg, type);
    const innerEnd = endOf(outerIsLeft ? mainWallInfo.rightSeg : mainWallInfo.leftSeg, type);
    let best: { outer: IShapePoint2d; inner: IShapePoint2d; score: number } | null = null;
    for (const w of infos) {
      if (!(w.shape instanceof Line2d)) continue;
      const line = extendSeg(w.seg);
      const oc = getNearestPoint(intersectCommonShape(outerShape, line) || [], junction);
      const ic = getNearestPoint(intersectCommonShape(innerShape, line) || [], junction);
      if (!oc || !ic) continue;
      const onSpan = (p: IPoint2d) => {
        const t = projT(p, w.seg.start, w.seg.end);
        return t >= -1e-6 && t <= 1 + 1e-6;
      };
      const outerCut = onSpan(oc);
      const innerCut = onSpan(ic);
      if (!outerCut && !innerCut) continue; // 两侧都够不到这条边界的线段 → 不是它挡的
      const outer = outerCut ? oc : outerEnd;
      const inner = innerCut ? ic : innerEnd;
      const score = Point2dUtils.distanceTo(outer, junction) + Point2dUtils.distanceTo(inner, junction);
      if (!best || score < best.score) best = { outer, inner, score };
    }
    if (!best) return null;
    const [lp, rp] = outerIsLeft ? [best.outer, best.inner] : [best.inner, best.outer];
    // 有一侧保持了自身偏移端点 → 两点不再与接头共线，端盖须经接头角点连成 3 点折线(与角点分支同形)。
    return isCollinear(lp, rp, junction) ? [lp, rp] : [lp, { x: junction.x, y: junction.y }, rp];
  }

  // calArcBoundaryFlatCut 的角点分支：接头是厚 0 边界的角点，且弧几乎沿其中一条边界的切向进入时，两条偏移边会
  // 各自戳过【不同】的边界线(如弧终点落在房间左下角、弧近乎与底墙相切：外偏移戳到底墙下方、内偏移戳到左墙外侧)。
  // 此时按单条线平切必然把另一条偏移留在房间外(外偏移那截墙体露在底墙下方)。做法：每条偏移各自裁回「它真正戳过的
  // 那条边界」，端盖经接头角点连成 3 点折线 —— 即墙体与边界围合区的布尔交，两侧都与边界齐平、无外露。
  // 只有两条偏移戳的是不同边界时才接管；同一条边界(或都没戳出)仍交回上面的「最紧那条线平切」。
  private calArcBoundaryCornerCut(
    mainWallInfo: IWallInfo,
    type: HandleType,
    infos: IWallInfo[],
    outerIsLeft: boolean
  ): IShapePoint2d[] | null {
    const junction = endOf(mainWallInfo.seg, type);
    const otherPt = farEndOf(mainWallInfo.seg, type);
    const leftSeg = mainWallInfo.leftSeg;
    const rightSeg = mainWallInfo.rightSeg;
    if (!(leftSeg instanceof ArcSeg2d) || !(rightSeg instanceof ArcSeg2d)) return null;
    const outerSeg = outerIsLeft ? leftSeg : rightSeg;
    const innerSeg = outerIsLeft ? rightSeg : leftSeg;
    // 该偏移端点是否戳过某条边界线段(落在墙体的反侧、且投影落在线段内)；戳过则沿本偏移圆退回到该边界线上的最近出口。
    const cutAtPokedBoundary = (offsetSeg: ArcSeg2d) => {
      const origin = endOf(offsetSeg, type);
      const originSweep = type === HandleType.Start ? 0 : offsetSeg.getSweepAngle();
      const circle = offsetSeg.createCircle();
      let hit: { id: string; point: IShapePoint2d; delta: number } | null = null;
      for (const w of infos) {
        if (!(w.shape instanceof Line2d)) continue;
        const a = w.seg.start;
        const b = w.seg.end;
        // 墙体所在侧(用本墙远端定基准)：偏移端点落在其反侧、且离线足够远，才算真的戳出边界。
        const sBody = signedDistanceToLine(a, b, otherPt);
        const sOrigin = signedDistanceToLine(a, b, origin);
        if (Math.abs(sOrigin) <= 0.01 || sOrigin * sBody >= 0) continue;
        const tOrigin = projT(origin, a, b);
        if (tOrigin < 0 || tOrigin > 1) continue; // 戳出的是这条线段的延长线之外 → 不是它挡的
        const ips = intersectCommonShape(circle, new Line2d({ points: { p1: a, p2: b } }));
        if (!ips) continue;
        for (const p of ips) {
          const t = projT(p, a, b);
          if (t < 0 || t > 1) continue; // 出口须落在这条边界线段内
          const s = sweepOnArc(offsetSeg, p);
          if (s === null) continue; // 须在本弧扫角内
          // 沿弧从偏移端点往回退时最先遇到的出口(不取离接头最近：弧近乎与边界相切时真出口可能远在弧上)。
          const delta = Math.abs(s - originSweep);
          if (!hit || delta < hit.delta) hit = { id: w.id, point: p, delta };
        }
      }
      return hit;
    };

    const outerHit = cutAtPokedBoundary(outerSeg);
    const innerHit = cutAtPokedBoundary(innerSeg);
    if (!outerHit || !innerHit || outerHit.id === innerHit.id) return null;
    return outerIsLeft
      ? [outerHit.point, { x: junction.x, y: junction.y }, innerHit.point]
      : [innerHit.point, { x: junction.x, y: junction.y }, outerHit.point];
  }

  // calArcBoundaryFlatCut/CornerCut 的「落在 body 中段」版本：隔墙端点落在厚 0 边界折线的【某条边 body 上】
  // (薄片/薄柱端头平盖的中点、圆头薄片的端头弧顶等)。此时本墙必有一条偏移端点戳过落脚那条边、埋进边界围出的
  // 实心区(薄片内 = 房间外)，而它【真正穿出边界的出口】往往不在落脚那条边上，而在它的一跳邻边上
  // (偏移端点戳进薄片端头内，沿本墙往前从薄片长边穿出)。
  // 通用 calPointWallIntersection 只拿落脚那条边当 cutter、且允许「延长形态」越界 2×墙厚 —— 于是在落脚边的
  // 【延长线】上取到远端幻影点(实测甩到端头角点外 107mm)，端盖横跨整个端头、还是压在薄片里。
  // 做法：以局部边界链(落脚边 + 公共角点 ≤6×墙厚的一跳厚 0 邻边)为切割集，每条偏移各自
  //   · 戳过边界 → 沿自身偏移边(弧→自身偏移圆 / 直→自身偏移线)退到「界内、沿本墙前向最先遇到」的出口；
  //   · 没戳过   → 保持自身偏移端点(它在房间侧，够不到边界，不该被无限延长线误裁)。
  // 端盖再沿边界从出口经【两条边界的公共角点】走回接头点 J，即墙带与边界围合区的布尔交：与边界齐平、不进薄片。
  // 落脚边是【弧】(圆头薄片端头)时，出口就在落脚边上也要接管：通用求交在那里要么完全不裁(直主墙)，
  // 要么把端盖拉成横跨圆头的弦(弧主墙，实测 7.4mm 压在薄片里)；本方法末尾统一过 markCapArcs，端盖沿端头弧走。
  // 出口/角点求不到、角点不在本墙带内、或走得不局部时返回 null，交回既有逻辑。
  private calOnBoundaryBodyCut(mainWallInfo: IWallInfo, type: HandleType): IShapePoint2d[] | null {
    const leftSeg = mainWallInfo.leftSeg;
    const rightSeg = mainWallInfo.rightSeg;
    const onWall = this.resolve(onWallOf(mainWallInfo, type));
    const atPoint = this.resolve(connectToPointOf(mainWallInfo, type));
    // 恰好落在【一条】厚 0 边界(直线或弧)的 body 上。
    if (onWall.length !== 1 || onWall[0].thickness !== 0) return null;
    if (!(onWall[0].shape instanceof Line2d) && !(onWall[0].shape instanceof Circle2d)) return null;
    // 接头处若有【更早建立】的有厚隔墙，本墙要被它们一起切 → 归 cutByEarlier / miter 那套，不在此接管。
    // 但只有【更晚】建立的隔墙时(本墙是该点最早的一条，如从薄片端头扇出的第一条)，按「先绘制不被后绘制切」，
    // 切它的只有边界 —— 此时本方法给出的边界绕角就是完整答案(通用求交在这里同样会取到落脚边延长线上的幻影点)。
    const mainOrder = this.orderOf(mainWallInfo.id);
    if (atPoint.some((w) => w.thickness > 0 && this.orderOf(w.id) < mainOrder)) return null;
    const jEdge = onWall[0];

    const junction = endOf(mainWallInfo.seg, type);
    const otherPt = farEndOf(mainWallInfo.seg, type);
    const base = [...onWall, ...atPoint];
    const chain = [...base, ...this.localBoundaryNeighbors(mainWallInfo, type, base)].filter(
      (w) => w.thickness === 0 && (w.shape instanceof Line2d || w.shape instanceof Circle2d)
    );

    // 该点是否在这条边界的「墙体反侧」(戳出侧)：直边按有符号侧、弧边按圆内外。
    const outsideOf = (w: IWallInfo, p: IPoint2d) => {
      if (w.seg instanceof ArcSeg2d) {
        const dp = Point2dUtils.distanceTo(p, w.seg.center) - w.seg.radius;
        const dBody = Point2dUtils.distanceTo(otherPt, w.seg.center) - w.seg.radius;
        return Math.abs(dp) > thresholdValue && dp * dBody < 0;
      }
      const s = signedDistanceToLine(w.seg.start, w.seg.end, p);
      return Math.abs(s) > thresholdValue && s * signedDistanceToLine(w.seg.start, w.seg.end, otherPt) < 0;
    };
    // 偏移端点是否戳过某条边界(落在其墙体反侧、且落在它的标称范围内；不能用无限延长形态)。
    const pokedBoundary = (origin: IPoint2d) => chain.some((w) => outsideOf(w, origin) && onSegSpan(w.seg, origin));
    // 沿自身偏移边退到边界上的出口：弧偏移用自身偏移圆 + 扫角窗口，直偏移用自身偏移线 + body 前向；
    // 出口都须落在该边界的标称范围内，取「沿本墙前向最先遇到」的那个。
    const bodyDir = Point2dUtils.getUnitDirection(junction, otherPt);
    const exitOnBoundary = (offsetSeg: IShapeSeg2d, origin: IPoint2d) => {
      const isArcOffset = offsetSeg instanceof ArcSeg2d;
      const offsetShape = extendSeg(offsetSeg);
      const originSweep = isArcOffset ? (type === HandleType.Start ? 0 : offsetSeg.getSweepAngle()) : 0;
      let hit: { edge: IWallInfo; point: IPoint2d; rank: number } | null = null;
      for (const w of chain) {
        const edgeShape = extendSeg(w.seg);
        const ips = intersectCommonShape(offsetShape, edgeShape);
        if (!ips) continue;
        for (const p of ips) {
          if (!onSegSpan(w.seg, p)) continue;
          let rank: number;
          if (isArcOffset) {
            const sw = sweepOnArc(offsetSeg, p);
            if (sw === null) continue;
            rank = Math.abs(sw - originSweep);
          } else {
            // 直偏移：只取 body 前向的出口(埋在实心区里的端点必须往前走才出得来，见"埋着的偏移端点须取前向出口")。
            rank = projectAlong(p, origin, bodyDir);
            if (rank < thresholdValue) continue;
          }
          if (!hit || rank < hit.rank) hit = { edge: w, point: p, rank };
        }
      }
      return hit;
    };
    const cutSide = (offsetSeg: IShapeSeg2d) => {
      const origin = endOf(offsetSeg, type);
      if (!pokedBoundary(origin)) return { point: { x: origin.x, y: origin.y }, edge: null as IWallInfo | null };
      const hit = exitOnBoundary(offsetSeg, origin);
      return hit ? { point: { x: hit.point.x, y: hit.point.y }, edge: hit.edge } : null;
    };
    const left = cutSide(leftSeg);
    const right = cutSide(rightSeg);
    if (!left || !right) return null; // 戳出了却找不到界内出口 → 不猜
    // 两条偏移端点都在边界实心区外(按端点判的 pokedBoundary 全不成立)，但端盖【面】仍可能擦过边界的一个角点：
    // 本墙从落脚边上离角点很近的地方斜着出发时，靠角点那侧的偏移端点会刚好从角点旁边溜出去(实测只差 1.5mm)，
    // 而端盖面把这个角点连同旁边那一小块实心区一起圈进了墙里。此时端盖须沿边界绕过该角点。
    if (!left.edge && !right.edge) return this.capAroundBoundaryCorner(mainWallInfo, type, chain, jEdge);
    // 出口就在落脚那条【直】边上(端点落在长房间墙 body 上的常规情形)：通用求交本来就切得对(还会把未戳侧延长贴齐
    // 成平口)，不在此接管。只有两种情形才接管：出口落在【一跳邻边】上(必须绕角)，或落脚边是【弧】(圆头薄片端头，
    // 通用求交在那里不裁/拉成弦)。
    const wrapNeeded = (left.edge && left.edge.id !== jEdge.id) || (right.edge && right.edge.id !== jEdge.id);
    if (!wrapNeeded && !(jEdge.seg instanceof ArcSeg2d)) return null;

    // 端盖沿边界从出口经公共角点走回接头点 J：出口不在落脚边上时，中间插入它与落脚边的公共角点。
    // undefined = 该侧无需绕角(出口就在落脚边上/未戳出)；null = 角点求不到或不在本墙带内(边界整条横穿墙带，
    // 此处的绕角不是本墙轮廓的一部分) → 整体不接管。
    const cornerBetween = (a: IWallInfo, b: IWallInfo): IPoint2d | null => {
      for (const pa of [a.seg.start, a.seg.end])
        for (const pb of [b.seg.start, b.seg.end]) if (Point2dUtils.equals(pa, pb)) return pa;
      return null;
    };
    const halfBand = mainWallInfo.thickness / 2 + thresholdValue;
    const wrapCorner = (edge: IWallInfo | null) => {
      if (!edge || edge.id === jEdge.id) return undefined;
      const c = cornerBetween(edge, jEdge);
      return c && isInsideBand(c, mainWallInfo.seg, halfBand) ? c : null;
    };
    const leftCorner = wrapCorner(left.edge);
    const rightCorner = wrapCorner(right.edge);
    if (leftCorner === null || rightCorner === null) return null;

    const cap: IShapePoint2d[] = [left.point];
    if (leftCorner) cap.push({ x: leftCorner.x, y: leftCorner.y });
    cap.push({ x: junction.x, y: junction.y });
    if (rightCorner) cap.push({ x: rightCorner.x, y: rightCorner.y });
    cap.push(right.point);

    // 相邻重合点合并、共线中间点去掉(两侧切在同一条边界上时退化成 2 点平口)。
    const cleaned: IShapePoint2d[] = [];
    for (const p of cap) {
      const last = cleaned[cleaned.length - 1];
      if (last && Point2dUtils.distanceTo(last, p) <= thresholdValue) continue;
      cleaned.push(p);
    }
    for (let i = cleaned.length - 2; i > 0; i--) {
      const [a, b, c] = [cleaned[i - 1], cleaned[i], cleaned[i + 1]];
      const len = Point2dUtils.distanceTo(a, c) || 1;
      if (Math.abs((c.x - a.x) * (b.y - a.y) - (c.y - a.y) * (b.x - a.x)) / len <= thresholdValue) {
        cleaned.splice(i, 1);
      }
    }
    if (cleaned.length < 2) return null;
    // 局部护栏：端盖不应沿长边界绕到远处(与 boundaryPocketCut 的 localLimit 同一量级)。
    const localLimit = Point2dUtils.distanceTo(junction, otherPt) + 10 * mainWallInfo.thickness;
    if (cleaned.some((p) => Point2dUtils.distanceTo(p, junction) > localLimit)) return null;
    // 沿弧边界(圆头薄片端头)走的那一段必须标成弧，不能拉成弦 —— 弦会横切进薄片里(见 markCapArcs)。
    return markCapArcs(cleaned, mainWallInfo, ...chain.map((w) => w.seg));
  }

  /**
   * calOnBoundaryBodyCut 的「端盖面擦过边界角点」分支：两条偏移端点都在厚 0 边界的实心区【外】，因此按偏移端点
   * 判的 pokedBoundary 全不成立、既有各分支都不接管；但落脚边的某个角点 K 落在本墙带内、且在端盖面前方 ——
   * 端盖面于是把 K 连同它旁边那一小块实心区圈进了墙里(实测薄片尖端 14.5mm 深、32.1mm 宽的三角)。
   * 端盖应沿边界绕过 K：[外侧偏移端点] → 接头 J →(沿落脚边)→ K →(沿 K 处另一条边界)→ 端盖面与该边界的交点
   * → [另一侧偏移端点]。角点唯一性、局部性、交点须落在端盖面内等条件不满足时返回 null，保持基础平口。
   */
  private capAroundBoundaryCorner(
    mainWallInfo: IWallInfo,
    type: HandleType,
    chain: IWallInfo[],
    jEdge: IWallInfo
  ): IShapePoint2d[] | null {
    if (jEdge.seg instanceof ArcSeg2d) return null; // 仅落脚在直边界上(弧落脚边由上面的出口分支处理)
    const junction = endOf(mainWallInfo.seg, type);
    const leftEnd = endOf(mainWallInfo.leftSeg, type);
    const rightEnd = endOf(mainWallInfo.rightSeg, type);
    const half = mainWallInfo.thickness / 2;
    const bodyDir = bodyDirectionOf(mainWallInfo.seg, type);
    const faceDir = Point2dUtils.getUnitDirection(junction, rightEnd); // 端盖面方向：中线 → 右偏移端点
    const forwardOf = (p: IPoint2d) => projectAlong(p, junction, bodyDir);
    const acrossOf = (p: IPoint2d) => projectAlong(p, junction, faceDir);

    // 落脚边上「严格落在本墙带内、且在端盖面前方一个墙厚以内」的角点。角点严格在带内 ⇒ 它周围的实心区必被墙盖住，
    // 所以这条判据本身就等价于「确有重叠」；更深的角点属于边界横穿墙体(clipStraightThroughBand 那套)，不在此接管。
    const corners = [jEdge.seg.start, jEdge.seg.end].filter(
      (c) =>
        forwardOf(c) > thresholdValue &&
        forwardOf(c) <= mainWallInfo.thickness &&
        Math.abs(acrossOf(c)) < half - thresholdValue
    );
    if (corners.length !== 1) return null;
    const corner = corners[0];

    // 角点处与落脚边相接的另一条边界 —— 端盖沿它离开实心区。
    const other = chain.find(
      (w) => w.id !== jEdge.id && (Point2dUtils.equals(w.seg.start, corner) || Point2dUtils.equals(w.seg.end, corner))
    );
    if (!other) return null;

    // 端盖面 ∩ 那条边界 = 墙带重新走出实心区的位置。
    const exit = getNearestPoint(intersectCommonShape(new LineSeg2d(leftEnd, rightEnd), other.seg) || [], corner);
    if (!exit) return null;
    // 出口须与角点同在中线的一侧、且比角点更靠外，否则绕出来的不是这个角。
    const sCorner = acrossOf(corner);
    const sExit = acrossOf(exit);
    if (sCorner * sExit <= 0 || Math.abs(sExit) <= Math.abs(sCorner) || Math.abs(sExit) > half + thresholdValue) {
      return null;
    }

    // 端盖按 left→right 排列：角点在右侧时 [左端点, J, 角点, 出口, 右端点]，在左侧时整体反过来。
    const jp: IShapePoint2d = { x: junction.x, y: junction.y };
    const k: IShapePoint2d = { x: corner.x, y: corner.y };
    const e: IShapePoint2d = { x: exit.x, y: exit.y };
    const cap = sCorner > 0 ? [leftEnd, jp, k, e, rightEnd] : [leftEnd, e, k, jp, rightEnd];

    // 相邻重合点合并(出口贴着偏移端点、角点贴着接头等退化情形)。
    const cleaned: IShapePoint2d[] = [];
    for (const p of cap) {
      const last = cleaned[cleaned.length - 1];
      if (last && Point2dUtils.distanceTo(last, p) <= thresholdValue) continue;
      cleaned.push({ x: p.x, y: p.y });
    }
    if (cleaned.length < 3) return null; // 退化成平口 → 交回既有逻辑
    // 沿弧边界走的那一段要标成弧，不能拉成弦。
    return markCapArcs(cleaned, mainWallInfo, ...chain.map((w) => w.seg));
  }

  // 通道/口袋类边界切(直墙、弧墙通用)：本墙端点落在厚 0 边界围出的槽/通道口，某偏移边戳进口袋——把端点当作「埋在边界折线里的
  // T 型接」交给通用 calPointWallIntersection(pointOnWall=true) 沿边界拼复合端盖。喂入局部边界链(接头边界墙 + 各端点一跳的厚 0
  // 邻居边界墙)；直线/弧邻居都只纳入「本墙偏移边沿 body 前向确会穿出它」的那条(否则 PathFinder 沿不相干的边界绕出远端)。
  // 判据：拼出的端盖须真戳进口袋(有点落在某边界的墙体反侧、戳出侧)且局部(不沿长边界绕到远处，防夸张)。coFanning(同边界更早扇出
  // 隔墙)一并纳入切割集算「边界切+互相切」复合端盖。不符则返回 null，由各调用方退回自己的兜底(直墙按边界线裁回 / 弧墙平切)。
  private boundaryPocketCut(
    mainWallInfo: IWallInfo,
    type: HandleType,
    infos: IWallInfo[],
    coFanning: IWallInfo[],
    assumePocket = false
  ): IShapePoint2d[] | null {
    const junction = endOf(mainWallInfo.seg, type);
    const otherPt = farEndOf(mainWallInfo.seg, type);
    const bodyDir = Point2dUtils.getUnitDirection(junction, otherPt);
    const leftOrigin = endOf(mainWallInfo.leftSeg, type);
    const rightOrigin = endOf(mainWallInfo.rightSeg, type);
    const arcExitForward = (w: IWallInfo): boolean => {
      const arc = w.seg as ArcSeg2d;
      const circle = arc.createCircle();
      for (const o of [leftOrigin, rightOrigin]) {
        const ips = intersectCommonShape(new Ray2d(o, bodyDir), circle, arcThresholdValue);
        if (!ips) continue;
        for (const p of ips) {
          const depth = projectAlong(p, o, bodyDir);
          if (depth < thresholdValue) continue;
          if (sweepOnArc(arc, p) === null) continue;
          return true;
        }
      }
      return false;
    };
    // 直线邻边同样要判「本墙偏移边沿 body 前向确会穿出它(界内)」——与 arcExitForward 对称，判据即
    // boundaryReachableForward。此前直线邻边无条件纳入：接头在薄片尖端时，尖端那条短边的一跳邻边里既有真正的
    // 穿出边(薄片另一条长边)，也有 6000mm 外挂在长边另一头的房间墙——后者在本墙【身后】、偏移边永远到不了，
    // 却会被 PathFinder 选成端盖端点(实测把切点甩到 12000mm 外的房间顶墙上)，整条端盖被 withinLocal 判废、
    // 退回按无限延长线裁切。
    // 仅对【直】主墙生效：弧主墙的 bodyDir 取的是首末点连线(弦)方向而非切向，用它发直线射线判可达性会判反
    // (弧插入窄槽的用例里会把真正的槽壁剔掉、端盖退化成平口)，弧主墙仍保持直线邻边全纳入。
    const gateLineNeighbors = !mainWallInfo.center;
    // 局部性：一跳邻边只在「它与接头边界的公共角点落在端盖尺度内(≤6×墙厚)」时才纳入——与 calStraightMultiCutByEarlier
    // 的 sharedCornerNearJunction 同一条不变量。可达性(lineExitForward)不足以拦住远处的邻边：本墙的另一端就落在那条
    // 房间墙上时它「沿 body 前向确会穿出」，于是被喂进 PathFinder，端盖顺着槽壁绕到 4000mm 外的房间墙上(整条墙被包住)。
    const cornerReach = mainWallInfo.thickness * 6;
    const chain = new Map<string, IWallInfo>(infos.map((d) => [d.id, d]));
    for (const nw of this.oneHopBoundaryNeighbors(infos, (n, seed) => {
      if (!sharedCornerWithin(seed.seg, n.seg, junction, cornerReach)) return false;
      if (n.shape instanceof Line2d) return !gateLineNeighbors || this.boundaryReachableForward(mainWallInfo, type, n);
      return n.shape instanceof Circle2d && n.seg instanceof ArcSeg2d && arcExitForward(n);
    })) {
      chain.set(nw.id, nw);
    }
    if (chain.size <= infos.length) return null; // 没有更多边界围成通道 → 非口袋

    const viaBoundary = this.calPointWallIntersection([...chain.values()], mainWallInfo, type, true);
    // 下面的局部护栏、戳出侧判据以及最终返回都要求它存在，取不到即判非口袋（等价于原来 withinLocal 为 false 的走向）
    if (!viaBoundary) return null;

    const localLimit = Point2dUtils.distanceTo(junction, otherPt) + 10 * mainWallInfo.thickness;
    const withinLocal = viaBoundary.every((p) => Point2dUtils.distanceTo(p, junction) <= localLimit);
    // assumePocket：调用方(如弧分支的 flatCoversMouth)已独立判定确为口袋，跳过下面的「戳出侧」判据(只保留局部护栏)。
    // 否则用「端盖点落在某边界的墙体反侧(戳出侧)」判据区分口袋 vs 贴房间墙裁回(lj)。注意墙远端与某边界共线时 sBody≈0、
    // 该边界判据退化——所以此判据只作「有 partition 混连时」的把关；assumePocket 场景绕过它。
    const pokesPocket =
      withinLocal &&
      (assumePocket ||
        viaBoundary.some((p) =>
          infos.some((b) => {
            const len = Point2dUtils.distanceTo(b.seg.start, b.seg.end) || 1;
            const sBody = crossOf(b.seg.start, b.seg.end, otherPt);
            const sP = crossOf(b.seg.start, b.seg.end, p);
            return Math.abs(sP) / len > 1 && sP * sBody < 0; // 落在边界的墙体反侧(戳出侧) → 确戳进口袋
          })
        ));
    if (!pokesPocket) return null;
    if (coFanning.length) {
      const withPartitions = new Map(chain);
      for (const p of coFanning) withPartitions.set(p.id, p);
      const viaCombined = this.calPointWallIntersection([...withPartitions.values()], mainWallInfo, type, true);
      if (viaCombined && viaCombined.length) return viaCombined;
    }
    return viaBoundary.length ? viaBoundary : null;
  }

  // 直隔墙端点只与「厚 0 边界线」相接(端点-端点共点，或端点落在边界线 body 上；接头处无其它有厚隔墙)：某条偏移边端点
  // 会戳过边界线、埋进边界外侧(如戳进相邻薄条/凹槽内)。用户语义：该接头「被边界切掉」。做法：把戳出的那条偏移边裁回它戳过
  // 的边界线，端盖经接头(墙中线端点)连接，使被裁那侧与边界齐平、墙仍贴合接头。只裁「确实戳过某条边界线段(投影落在该线段内、
  // 且落在墙体的反侧)」的偏移端点——朝开放房间一侧、没戳过任何边界线段的偏移端点保持不动(避免用无限直线误裁到开放侧)。
  // degenerated: 上游通用端盖逻辑(miter/互切)已全部退化、一个端盖点都没给出。此时即使接头处连着有厚隔墙，也要在此按边界裁切
  // 接管，否则端盖保持基础平口、戳过厚 0 边界(见下方 partitions 判据)。
  private calStraightBoundaryFlatCut(
    mainWallInfo: IWallInfo,
    type: HandleType,
    degenerated: boolean = false
  ): IShapePoint2d[] | null {
    if (mainWallInfo.center || !(mainWallInfo.shape instanceof Line2d)) return null; // 仅处理直隔墙
    const connected = connectToPointOf(mainWallInfo, type);
    // 端点落在其 body 上的边界线(T 型接厚 0 边界线)也算——通用 T 型端盖对厚 0 墙求不到交点、会退化成平口不裁。
    const onWall = onWallOf(mainWallInfo, type);
    const connectedAll = this.resolve([...connected, ...onWall]);
    if (!connectedAll.length) return null;
    const infos = connectedAll.filter((w) => w.thickness === 0 && w.shape instanceof Line2d); // 厚 0 边界直线
    if (!infos.length) return null; // 接头处无厚 0 边界 → 纯隔墙接头，另有处理
    // 接头处直接相接的厚 0 弧边界暂不在此处理(本方法按直线边界链拼)。
    if (connectedAll.some((w) => w.thickness === 0 && !(w.shape instanceof Line2d))) return null;
    // 接头处相连的有厚隔墙：仅纳入「更早建立、且与本墙落在同一条边界 body 上(从同一点扇出，如都在槽底 UXxB 上)」的那些，
    // 作为切割集的一部分——按「先建切后建」，后建墙(本墙)被更早的同扇出墙切；更早的墙不被后建墙切(方向不能反)。
    // 其它「隔墙 + 边界」混连(普通隔墙接头、贴房间墙的链等)不属扇出，靠下方「戳进口袋」判据把关，非口袋则交回原有互切逻辑。
    const mainOrder = this.orderOf(mainWallInfo.id);
    const onBoundaryIds = new Set(onWall.map((w) => w.id).filter((id) => infos.some((b) => b.id === id)));
    const partitions = connectedAll.filter((w) => w.thickness > 0);
    const coFanning = partitions.filter((p) => {
      if (this.orderOf(p.id) >= mainOrder) return false; // 只被更早建立的同扇出墙切(先建切后建)
      const pr = p.relativeWallInfos;
      const pOn = [...(pr?.mainStartOnWallInternal || []), ...(pr?.mainEndOnWallInternal || [])];
      return pOn.some((w) => onBoundaryIds.has(w.id));
    });

    const junction = endOf(mainWallInfo.seg, type);
    const otherPt = farEndOf(mainWallInfo.seg, type);
    const bodyDir = Point2dUtils.getUnitDirection(junction, otherPt);
    const leftOrigin = endOf(mainWallInfo.leftSeg, type);
    const rightOrigin = endOf(mainWallInfo.rightSeg, type);

    // 通道/口袋类：把端点当作埋在边界折线里的 T 型接，沿边界拼复合端盖(直墙/弧墙通用)。
    const pocket = this.boundaryPocketCut(mainWallInfo, type, infos, coFanning);
    if (pocket && pocket.length) return pocket;
    // 有相连隔墙但非口袋扇出(如普通隔墙接头、贴房间墙链) → 交回原有互切/miter 逻辑。
    // 但那两条路在下面两种构型下会全部退化、一个端盖点都给不出，端盖就只剩基础平口、直接戳过厚 0 边界：
    //   · 本墙是该接头【最早】建立的隔墙——互切按「先建切后建」不处理它，更早隔墙又不存在；
    //   · 本墙与更早隔墙近平行(接近共线续接)——沿更早墙轮廓求不到局部交点(calPointWallIntersection 返回 null)。
    // 这两种都由调用处的 degenerated 标记出来，此时在此接管，把戳过边界的偏移边裁回边界。
    if (partitions.length && !degenerated) return null;

    // 无通道口袋：按边界线段把戳出的偏移边裁回(简单角点/贴边)。
    // 把 origin 处的偏移边(沿 bodyDir 的直线)裁回它戳过的最近边界线；没戳过任何边界线段则返回 null(不裁)。
    // 同时带回裁到的那条边界墙，供未戳侧延长贴齐使用。
    const cutOffset = (origin: IPoint2d): { point: IShapePoint2d; wall: IWallInfo } | null => {
      const offLine = new Line2d({ points: { p1: origin, p2: { x: origin.x + bodyDir.x, y: origin.y + bodyDir.y } } });
      let best: { point: IShapePoint2d; depth: number; wall: IWallInfo } | null = null;
      for (const w of infos) {
        const a = w.seg.start;
        const b = w.seg.end;
        const sBody = Math.sign(crossOf(a, b, otherPt));
        const sP = Math.sign(crossOf(a, b, origin));
        if (sP === 0 || sP === sBody) continue; // 未戳过这条边界(与墙体同侧/在线上)
        const t = projT(origin, a, b);
        if (t < -1e-6 || t > 1 + 1e-6) continue; // 偏移端点不在该边界线段的投影范围内 → 非真正戳过该段
        const q = getNearestPoint(
          intersectCommonShape(offLine, new Line2d({ points: { p1: a, p2: b } })) || [],
          origin
        );
        if (!q) continue;
        const depth = Point2dUtils.distanceTo(q, origin);
        if (!best || depth < best.depth) best = { point: { x: q.x, y: q.y }, depth, wall: w };
      }
      return best ? { point: best.point, wall: best.wall } : null;
    };

    // 未戳过边界的那一侧偏移边(origin 落在边界室内侧)：沿 body 反向延长到指定边界线。
    // 仅当延长交点落在该边界线段范围内(projT ∈ [0,1])时才返回延长点——超出线段(如落在角点外)则返回 null 不延长，
    // 使「端点落边界 body 中段」延长贴齐成平口，而「端点落边界角点」延长会越出邻边、保持原折线端盖。
    const extendOffsetTo = (origin: IPoint2d, boundary: IWallInfo): IShapePoint2d | null => {
      const a = boundary.seg.start;
      const b = boundary.seg.end;
      const offLine = new Line2d({ points: { p1: origin, p2: { x: origin.x + bodyDir.x, y: origin.y + bodyDir.y } } });
      const q = getNearestPoint(intersectCommonShape(offLine, new Line2d({ points: { p1: a, p2: b } })) || [], origin);
      if (!q) return null;
      const t = projT(q, a, b);
      if (t < -1e-6 || t > 1 + 1e-6) return null; // 延长交点落在边界线段外 → 不延长
      return { x: q.x, y: q.y };
    };

    const leftCut = cutOffset(leftOrigin);
    const rightCut = cutOffset(rightOrigin);
    if (!leftCut && !rightCut) return null; // 无偏移端点戳过边界 → 交回通用逻辑(普通平口)

    let lp: IShapePoint2d = leftCut?.point ?? leftOrigin;
    let rp: IShapePoint2d = rightCut?.point ?? rightOrigin;
    // 一侧戳过边界被裁回、另一侧未戳过：把未戳侧沿 body 反向延长到同一条边界线(延长点须在该边界线段内)，
    // 使裁切两端与边界齐平(平整)；延长越出边界线段(如角点情形)则保持原偏移端点、走折线端盖。
    if (leftCut && !rightCut) {
      const ext = extendOffsetTo(rightOrigin, leftCut.wall);
      if (ext) rp = ext;
    } else if (rightCut && !leftCut) {
      const ext = extendOffsetTo(leftOrigin, rightCut.wall);
      if (ext) lp = ext;
    }
    // 端盖经接头角点连接：裁掉一侧后接头不再与两偏移端点共线 → 成折线贴合边界角；未变形则接头共线、退化为两点平口。
    const collinear = isCollinear(lp, rp, junction);
    return collinear ? [lp, rp] : [lp, junction, rp];
  }

  // 直隔墙(厚>0)端点落在「厚 0 弧边界 + 厚 0 直边界」共点的接头角上(如薄弧带/拱的端头：外/内弧 + 端头平盖直线在此相交)：
  // 隔墙自该角点沿切向伸出，它的某条偏移边端点会戳进弧带材料内(在该弧圆的「带一侧」)。用户语义：该接头被墙边界切掉。
  // calStraightBoundaryFlatCut 只按直线边界链拼、遇到接头处有厚 0 弧边界会整体退回不裁 → 这类弧带端头接头此前完全没被切。
  // 做法：判定哪条偏移端点戳进带内(与本墙远端分居该弧圆两侧),把它沿自身偏移线裁回「最近的一条接头边界(直边界线段/弧段)」,
  // 端盖经接头角点连成折线;未戳进带内、朝房间那侧的偏移端点保持原样。仅当接头处全是厚 0 边界(无厚>0 隔墙)、且至少一条是弧时
  // 才在此处理(纯直边界接头仍走 calStraightBoundaryFlatCut,厚>0 弧/隔墙接头另有相切/miter/cutByEarlier 逻辑)。
  private calStraightArcBoundaryFlatCut(mainWallInfo: IWallInfo, type: HandleType): IShapePoint2d[] | null {
    if (mainWallInfo.center || !(mainWallInfo.shape instanceof Line2d)) return null; // 仅处理直隔墙
    const infos = this.resolve(connectToPointOf(mainWallInfo, type));
    if (!infos.length) return null;
    if (infos.some((w) => w.thickness > 0)) return null; // 有厚>0 隔墙 → 另有处理(相切/miter/cutByEarlier)
    // 用类型守卫过滤：把 instanceof 的结论带进元素类型，下面取 center/radius/弧段就不必再断言
    const arcs = infos.filter(
      (w): w is IWallInfo & { shape: Circle2d; seg: ArcSeg2d } =>
        w.thickness === 0 && w.shape instanceof Circle2d && w.seg instanceof ArcSeg2d
    );
    const lines = infos.filter((w) => w.thickness === 0 && w.shape instanceof Line2d);
    if (!arcs.length) return null; // 无厚 0 弧边界 → 交回 calStraightBoundaryFlatCut(纯直边界)

    const junction = endOf(mainWallInfo.seg, type);
    const otherPt = farEndOf(mainWallInfo.seg, type);
    const bodyDir = Point2dUtils.getUnitDirection(junction, otherPt);
    const leftOrigin = endOf(mainWallInfo.leftSeg, type);
    const rightOrigin = endOf(mainWallInfo.rightSeg, type);

    // 偏移端点是否戳进某条接头弧的带内:该端点与本墙远端(otherPt)分居该弧圆两侧——弧圆一侧是带材料、另一侧是房间。
    const inBand = (o: IPoint2d): boolean =>
      arcs.some((arc) => {
        const c = arc.shape.center;
        const r = arc.shape.radius;
        const dO = Point2dUtils.distanceTo(o, c) - r;
        const dOther = Point2dUtils.distanceTo(otherPt, c) - r;
        return Math.abs(dO) > thresholdValue && dO * dOther < 0;
      });

    // 把 origin 沿自身偏移线【向 body 前进方向】裁到它「穿出弧带材料的那条边界」——取最近的一个前向交点(depth>0):
    // origin 埋在带内,沿 bodyDir 前进遇到的第一条接头边界(直边界线段内 / 弧段内)即它离开带、进入房间的出口。
    // 不能取最近(不分方向):隔墙近乎与弧相切时,后方端头平盖上会有个很近的交点(几 mm),而真正埋进带里的那截要一直到
    // 前方穿出外/内弧处(可达上百 mm),取后方点会几乎不裁、漏掉沿切向长距离埋进弧带的那截。
    const clipForward = (origin: IPoint2d): IShapePoint2d | null => {
      const ray = new Ray2d(origin, bodyDir);
      let best: IShapePoint2d | null = null;
      let bestDepth = Infinity;
      const consider = (p: IPoint2d) => {
        const depth = projectAlong(p, origin, bodyDir);
        if (depth < thresholdValue || depth >= bestDepth) return;
        bestDepth = depth;
        best = { x: p.x, y: p.y };
      };
      for (const w of lines) {
        const a = w.seg.start;
        const b = w.seg.end;
        const ips = intersectCommonShape(ray, new LineSeg2d(a, b));
        if (!ips) continue;
        for (const p of ips) {
          const t = projT(p, a, b);
          if (t < -1e-6 || t > 1 + 1e-6) continue; // 落在边界线段外
          consider(p);
        }
      }
      for (const arc of arcs) {
        const seg = arc.seg;
        const ips = intersectCommonShape(ray, seg.createCircle(), arcThresholdValue);
        if (!ips) continue;
        for (const p of ips) {
          if (sweepOnArc(seg, p) === null) continue; // 落在弧段外
          consider(p);
        }
      }
      return best;
    };

    const lIn = inBand(leftOrigin);
    const rIn = inBand(rightOrigin);
    if (!lIn && !rIn) return null; // 无偏移端点戳进带内 → 交回默认平口

    let lp: IShapePoint2d = leftOrigin;
    let rp: IShapePoint2d = rightOrigin;
    if (lIn) lp = clipForward(leftOrigin) ?? leftOrigin;
    if (rIn) rp = clipForward(rightOrigin) ?? rightOrigin;
    if (Point2dUtils.equals(lp, leftOrigin) && Point2dUtils.equals(rp, rightOrigin)) return null; // 实际未裁到 → 不改写

    // 端盖经接头角点连成 3 点折线 [裁后左, 接头角点, 裁后右]：被裁那侧偏移边(裁到弧带出口)与接头角点同在该弧上、连线即弧的弦,
    // 与弧带边界齐平(平整);另一段接头角点→朝房间的偏移端点即室内侧平口。接头与两点共线时退化为 2 点平口。
    const collinear = isCollinear(lp, rp, junction);
    return collinear ? [lp, rp] : [lp, { x: junction.x, y: junction.y }, rp];
  }

  // 后建直隔墙是浮空接头处「第三条及以后」建立的墙(更早相连隔墙≥2、弧/直混合)：本直墙被「所有更早的墙」轮廓切割。
  // (前两条按角平分线互相 miter，见 calPointPointConnected；第三条+ 走此处被更早轮廓切，与 calArcJunctionCap 的弧主墙对称。)
  // 做法：把本直墙当作「端点埋在更早墙轮廓里的 T 型接」，交给通用 calPointWallIntersection(pointOnWall=true)——
  // 它把左右两条偏移边都拼到更早墙朝本体那一侧的近边上(必要时经 PathFinder 拼复合端盖)，两条偏移边都会被裁到更早轮廓、
  // 不会有某条边停在自身偏移端点而戳进更早墙体(此前只按 body 方向单向射线切时，朝接头反侧的那条边求不到出口、会漏切嵌入)。
  // 只喂「更早建立的隔墙」作切割对象(不含后建墙、不含房间墙)——避免通用求交把偏移边算到后建墙/远端而绕缠。
  private calStraightMultiCutByEarlier(
    mainWallInfo: IWallInfo,
    type: HandleType,
    connectedInfos: { id: string }[]
  ): IShapePoint2d[] | null {
    if (mainWallInfo.center || !(mainWallInfo.shape instanceof Line2d)) return null; // 仅处理直隔墙
    const mainOrder = this.orderOf(mainWallInfo.id);
    const onWall = onWallOf(mainWallInfo, type);
    const onBoundaryBody = onWall.some((w) => this._cacheWallMap.get(w.id)?.thickness === 0); // 接头落在厚 0 边界 body 上
    // 接头处的相连墙候选：用「本接头端点的原始 connectToPoint 列表」(含被上层当孪生/透镜排除的墙——如把本直墙当弦的
    // 穹顶弧，它共用本墙的另一端点)并上传入的 connectedInfos。孪生穹顶弧在近接头处同样形成先绘制的竖直带，须作为更早
    // cutter；上层的孪生排除只为跳过端盖 miter，不该让它在被切时漏掉这条轮廓、cutter 不足而退回通用逻辑绕缠。
    const rawConnectToPoint = connectToPointOf(mainWallInfo, type);
    const candidateInfos = [...connectedInfos, ...rawConnectToPoint];
    const seenIds = new Set<string>();
    const earlierAll = candidateInfos
      .map((w) => this._cacheWallMap.get(w.id))
      .filter((w): w is IWallInfo => {
        if (!w || w.thickness <= 0 || w.id === mainWallInfo.id || this.orderOf(w.id) >= mainOrder) return false;
        if (seenIds.has(w.id)) return false;
        seenIds.add(w.id);
        return true;
      });
    // 排除「与同组另一条更早墙共用两个端点」的弧(它是那条墙的弦上穹顶/透镜伙伴，弧身鼓向接头另一侧)：
    // 弦墙本身已提供接头处的墙厚近边、给出正确平切；把这条鼓向另一侧的弧也当 cutter 会让 PathFinder 绕着透镜
    // 走到远处(如绕到弦的另一端接头)、端盖乱缠。弦墙(直)保留、穹顶(弧)剔除。
    const earlier = earlierAll.filter(
      (w) =>
        !(w.shape instanceof Circle2d && earlierAll.some((o) => o.id !== w.id && sharesBothEndpoints(w.seg, o.seg)))
    );
    // 一般浮空接头：前两条互相 miter，只有第三条及以后才在此被更早轮廓切(earlier≥2)。
    // 但接头「搭接在厚 0 墙边界」上(多条隔墙都从同一边界角点/边界搭出)时，各隔墙不是互相 miter，而是各自贴着边界搭接；
    // 此时后绘制的隔墙应被【更早绘制的隔墙】沿其轮廓切割(用户语义：多条搭接在墙边界，后画的被先画的切)——只需 1 条更早隔墙即可。
    const boundaries = [
      ...new Map(
        this.resolve(candidateInfos)
          .filter((w) => w.thickness === 0)
          .map((w) => [w.id, w] as const)
      ).values()
    ];
    // 接头落在边界 body 上(T 型接)时相连列表里只有那一条边界线，但本墙的偏移边可能是从它的一跳厚 0 邻边穿出的
    // (如薄柱端头：偏移边越过柱端线的角点、实际从柱的侧边穿出)。把这些邻边一并纳入切割集，与 boundaryPocketCut 的
    // 边界链一致；否则该侧求不到交点，只能按柱端线的无限延长线裁到角点之外。
    if (onBoundaryBody) {
      boundaries.push(...this.oneHopBoundaryNeighbors(boundaries, (nw) => nw.shape instanceof Line2d));
    }
    // 接头处的厚 0 边界(薄片/薄柱/弧带的一条边)常常不是本墙偏移边真正的穿出边——真正的穿出边是它【隔着一个角点的
    // 那条邻边】(薄片的另一条长边、弧带另一侧的弧)。邻边不在相连列表里 → 该侧求不到出口、并集轮廓在那里断开、
    // PathFinder 拼不出通路，端盖整个退化成「只按边界切」甚至完全不裁(更早隔墙那一刀被静默丢掉)。
    // 补邻边的两条判据(都必须满足)：
    //   · 局部性：两条边界的公共角点必须落在端盖尺度内(≤6×墙厚，与下面 maxReach 同一常量)。端盖只能绕过接头附近
    //     的角点；实测所有真正需要的邻边其公共角点都 ≤1.43×墙厚(薄片/薄柱/弧带的宽度量级)，而「边界另一头挂着的
    //     房间墙」都 ≥9.7×墙厚，两者之间没有中间值。少了这条闸，普通「贴房间墙拐角」的裁切会被多喂的远处邻边改掉。
    //   · 可达性：本墙偏移边沿 body 前向确会穿出它(界内)，与 boundaryPocketCut 的 arcExitForward 同一判据。
    const junctionPt = endOf(mainWallInfo.seg, type);
    const cornerReach = mainWallInfo.thickness * 6;
    boundaries.push(
      ...this.oneHopBoundaryNeighbors(
        boundaries,
        (nw, seed) =>
          sharedCornerWithin(seed.seg, nw.seg, junctionPt, cornerReach) &&
          this.boundaryReachableForward(mainWallInfo, type, nw)
      )
    );
    const hasBoundaryAtJunction = boundaries.length > 0;
    const minEarlier = hasBoundaryAtJunction ? 1 : 2;
    if (earlier.length < minEarlier) return null;
    // 接头落在厚 0 边界 body 上(T 型接边界线)时，常规是交给「端点落墙」那套(沿边界拼端盖 / 按边界线裁回偏移边)。
    // 但那套只按边界裁：本墙某条偏移端点若还埋在某条更早隔墙的实心带里，就会停在带内、盖住先画的隔墙。
    // 用户语义：被墙边界切割后，还要被先绘制的隔墙轮廓再切。故仅在「确有偏移端点埋进更早隔墙带内」时在此接管
    // (把边界线一并当 cutter，两侧分别贴合边界与更早轮廓)；没埋进去则维持原样交回，不影响普通贴边界 T 型接。
    if (onBoundaryBody) {
      const leftEnd = endOf(mainWallInfo.leftSeg, type);
      const rightEnd = endOf(mainWallInfo.rightSeg, type);
      // 与本墙共用两端点的平行孪生隔墙(透镜配置)不算：它的带在共享端点处必然贴着本墙的偏移端点，
      // 但两条孪生墙本就互不裁剪(见 calExtremeOutline 的 isParallelTwin)。
      const buriedInEarlier = earlier
        .filter((w) => !sharesBothEndpoints(w.seg, mainWallInfo.seg))
        .some((w) => [leftEnd, rightEnd].some((p) => isInsideBand(p, w.seg, w.thickness / 2)));
      if (!buriedInEarlier) return null;
    }

    // 接头搭在墙边界上时,后画隔墙要「在被边界切割的基础上再被更早隔墙切割」——把厚 0 边界墙一并作为 cutter,
    // 端盖同时贴合边界线(某侧偏移被边界裁回)与更早隔墙轮廓(另一侧偏移被先画隔墙裁回)。
    // calPointWallIntersection 已能混合处理厚 0 边界(boundaryLines)与厚>0 隔墙(occluders)。
    const cutters = hasBoundaryAtJunction ? [...earlier, ...boundaries] : earlier;
    const cap = this.calPointWallIntersection(cutters, mainWallInfo, type, true);
    if (!cap || cap.length < 2) return cap;
    // 端盖左→右排列：cap[0] 对应左偏移边终点、cap[末] 对应右偏移边终点。某条偏移边与它「本该切到的那条更早墙」近平行，
    // 要延长到大弧远处才求到交点(该侧终点被拉到离接头很远)——不追这个远交点，改用**连接处的墙厚端盖面**把这条边平切过来：
    // 更早墙在接头 J 处的「墙厚轮廓」= 连接它在 J 的两条偏移端点的那条线段(垂直其中线、宽=墙厚)。把本墙这条偏移边(延长成
    // 直线)与各更早墙的该端盖面求交，取离接头最近的交点，本墙这条边就沿连接处的墙厚面平切齐平(不会切到相邻墙的侧边线上
    // 留下奇怪的小折角，也不追大弧远端)。附近实在无交点才退回自身偏移端点(平口)。阈值 6×墙厚，与 calArcJunctionCap 的
    // maxMiter 同量级(合法端盖点实测 ≤1×墙厚)。
    const junction = endOf(mainWallInfo.seg, type);
    const maxReach = mainWallInfo.thickness * 6;
    // 用连接处的墙厚端盖面(endCapFaceAt)，把「延长太远」的本墙偏移边平切过来。
    const cutAcross = (offsetSeg: IShapeSeg2d, offsetOrigin: IPoint2d): IShapePoint2d => {
      const offLine = new Line2d({ points: { p1: offsetSeg.start, p2: offsetSeg.end } });
      const cands = earlier.flatMap((w) => intersectCommonShape(offLine, endCapFaceAt(w, junction)) || []);
      return nearestWithinReach(cands, junction, maxReach, offsetOrigin);
    };
    const leftOrigin = endOf(mainWallInfo.leftSeg, type);
    const rightOrigin = endOf(mainWallInfo.rightSeg, type);
    const out = cap.slice();
    let changed = false;
    if (Point2dUtils.distanceTo(out[0], junction) > maxReach) {
      out[0] = cutAcross(mainWallInfo.leftSeg, leftOrigin);
      changed = true;
    }
    if (Point2dUtils.distanceTo(out[out.length - 1], junction) > maxReach) {
      out[out.length - 1] = cutAcross(mainWallInfo.rightSeg, rightOrigin);
      changed = true;
    }
    // 更早墙的【实际轮廓】：原始偏移边(各按自身范围，弧为弧段) + 它在接头处的墙厚端盖面。用原始偏移边而不是已算端盖的
    // drawShape：与计算顺序无关，绘制增量与打开全量一致。
    const outlineSegs: IShapeSeg2d[] = [];
    for (const w of earlier) {
      outlineSegs.push(w.leftSeg, w.rightSeg);
      const atStart = Point2dUtils.equals(w.seg.start, junction);
      if (atStart || Point2dUtils.equals(w.seg.end, junction)) {
        outlineSegs.push(
          new LineSeg2d(atStart ? w.leftSeg.start : w.leftSeg.end, atStart ? w.rightSeg.start : w.rightSeg.end)
        );
      }
    }
    // 厚 0 边界也是 cutters 的一部分(见上面的 cutters)，它们的边同样是合法的「被切到的轮廓」。
    // 漏掉的话，凡是切在边界上的端盖点都会被下面的 restoreIfUncut 判成假点、恢复成本墙自身的偏移端点 ——
    // 那一侧于是完全不裁，偏移端点仍埋在薄片/薄柱里。cutters 与判「是否真被切到」的轮廓集合必须一致。
    for (const b of boundaries) outlineSegs.push(b.seg);
    const outlineTol = mainWallInfo.thickness * 0.02;
    const onOutline = (p: IPoint2d) =>
      outlineSegs.some((s) => {
        if (s instanceof ArcSeg2d) {
          if (Math.abs(Point2dUtils.distanceTo(p, s.center) - s.radius) > outlineTol) return false;
          let sw = s.getPointSweepAngle(p);
          if (sw > 2 * Math.PI - arcThresholdValue) sw -= 2 * Math.PI;
          const slack = outlineTol / (s.radius || 1);
          return sw >= -slack && sw <= s.getSweepAngle() + slack;
        }
        const dx = s.end.x - s.start.x;
        const dy = s.end.y - s.start.y;
        const len = Math.hypot(dx, dy) || 1;
        const t = ((p.x - s.start.x) * dx + (p.y - s.start.y) * dy) / (len * len);
        if (t < -outlineTol / len || t > 1 + outlineTol / len) return false;
        return Math.abs((dx * (p.y - s.start.y) - dy * (p.x - s.start.x)) / len) <= outlineTol;
      });
    // 端盖端点落在更早轮廓的【延长线】上(不在任何更早墙的真实轮廓范围内)、而本墙这条偏移端点又没埋进任何更早墙体：
    // 这一侧其实没被任何更早轮廓切/斜接，那个交点是假点(本例本墙与两条相切弧的平端盖面近平行，交点落在平台面外
    // 208≈3×墙厚处，端盖横穿出去悬空)。恢复本墙自身的偏移端点，端盖再经接头 J 折过去贴合先画墙的轮廓。
    const restoreIfUncut = (index: number, origin: IPoint2d) => {
      if (onOutline(out[index])) return false;
      if (earlier.some((w) => isInsideBand(origin, w.seg, w.thickness / 2))) return false; // 端点确实埋着 → 仍需切
      out[index] = { x: origin.x, y: origin.y };
      changed = true;
      return true;
    };
    const restoredLeft = restoreIfUncut(0, leftOrigin);
    const restoredRight = restoreIfUncut(out.length - 1, rightOrigin);
    // 恢复了自身偏移端点的那一侧「保持原有长度」：端盖不该再越过接头往本墙【身后】伸(那是斜接才会做的事，而这一侧
    // 压根没有与更早轮廓的交点)。把紧邻该侧、落在接头端盖面身后(沿 body 方向投影为负)的过渡点丢掉，端盖止于接头 J。
    const otherPt = farEndOf(mainWallInfo.seg, type);
    const bodyDir = Point2dUtils.getUnitDirection(junction, otherPt);
    const forwardOf = (p: IPoint2d) => bodyDir.x * (p.x - junction.x) + bodyDir.y * (p.y - junction.y);
    const behindTol = mainWallInfo.thickness * 0.02;
    if (restoredLeft) {
      while (out.length > 2 && forwardOf(out[1]) < -behindTol) out.splice(1, 1);
    }
    if (restoredRight) {
      while (out.length > 2 && forwardOf(out[out.length - 2]) < -behindTol) out.splice(out.length - 2, 1);
    }
    // 恢复的那一侧端盖须经接头 J 才能与先画墙的轮廓接上(否则自身偏移端点直连到另一侧切点会斜跨、把接头处的墙体切掉)。
    if (!out.some((p) => Point2dUtils.distanceTo(p, junction) <= thresholdValue)) {
      if (restoredLeft) out.splice(1, 0, { x: junction.x, y: junction.y });
      else if (restoredRight) out.splice(out.length - 1, 0, { x: junction.x, y: junction.y });
    }
    if (!changed) return out;
    // 被平切那一侧，先前沿更早弧偏移边走向远端时会留下带弧信息(center/clockwise)的过渡点。平切后端盖应是直线段，
    // 这些点既多余又会让最后一条直墙的端盖出现弧形。去掉与相邻两点共线的中间点、并抹掉残留弧信息，端盖恢复为纯折线。
    const collinearTol = mainWallInfo.thickness * 0.05;
    const cleaned: IShapePoint2d[] = [{ x: out[0].x, y: out[0].y }];
    for (let i = 1; i < out.length - 1; i++) {
      const a = cleaned[cleaned.length - 1];
      const b = out[i];
      const c = out[i + 1];
      const base = Point2dUtils.distanceTo(a, c);
      const cross = Math.abs((b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x));
      if (base > thresholdValue && cross / base < collinearTol) continue; // 与相邻点共线 → 丢弃该过渡点
      cleaned.push({ x: b.x, y: b.y }); // 抹掉 center/clockwise：平切端盖为直线段
    }
    cleaned.push({ x: out[out.length - 1].x, y: out[out.length - 1].y });
    return cleaned;
  }

  // 弧形隔墙(后建、最后一条)端点搭接在「厚 0 墙边界」上、且该接头还连着≥1 条更早建立的有厚隔墙——多条隔墙搭接在
  // 同一墙边界上。与直墙版 calStraightMultiCutByEarlier 对称：多条隔墙搭接在墙边界时不互相 miter，后画的被先画的沿其
  // 轮廓切。通用 calMixConnected → calPointWallIntersection 的 PathFinder 会沿更早直墙的整条 body 绕行(端盖从接头
  // 一路拖到墙的另一头再绕回，轮廓自交)。这里按几何确定性求解：把本弧内、外两条偏移圆各自裁到「离接头最近、且不陷进任一
  // 先画墙 body」的一条切割边——更早隔墙【对着本弧的那一面轮廓】(端点相连时=它在接头的端盖面;body 搭接时=其偏移边)
  // + 厚 0 边界线——上，得到齐平贴合边界/先画墙轮廓面的端盖；两切点与接头不共线时(先画墙面与边界成角)经接头点连成 3 点
  // 折线,与直墙版一致。仅该配置(弧 + 更早有厚隔墙 + 厚 0 边界)返回结果,其它情形(浮空弧接头、纯边界平切、相切续接等)返回 null。
  private calArcMultiCutByEarlier(
    mainWallInfo: IWallInfo,
    type: HandleType,
    connectedInfos: { id: string }[]
  ): IShapePoint2d[] | null {
    if (!(mainWallInfo.shape instanceof Circle2d) || !(mainWallInfo.seg instanceof ArcSeg2d)) return null; // 仅弧隔墙
    if (!(mainWallInfo.leftShape instanceof Circle2d) || !(mainWallInfo.rightShape instanceof Circle2d)) return null;
    const mainOrder = this.orderOf(mainWallInfo.id);
    const infos = [
      ...new Map(
        connectedInfos
          .map((w) => this._cacheWallMap.get(w.id))
          .filter((w): w is IWallInfo => !!w)
          .map((w) => [w.id, w] as const)
      ).values()
    ];
    // 接头须搭在厚 0 墙边界上(「搭接在墙边界」信号——否则是浮空弧接头,由 calArcJunctionCap 处理)。
    // 边界含直线段与弧段两种(薄环带的封口线是直线、环带两侧是弧)。
    const boundaries = infos.filter((w) => w.thickness === 0);
    if (!boundaries.length) return null;
    // 本弧端点落在某条厚 0 边界的 body 上(如落在薄环带端头封口线的中点)时，本弧的偏移边往往从该边界的【相邻一条边】
    // 穿出(如向下穿出环带的外侧弧)。只用直接相连的那条边界裁不到——穿出边不在集合里。补上这些边界的「端点相连一跳
    // 邻边(厚 0，直线或弧都要)」一并作切割边，与 clipStraightThroughBand 的同类处理一致。
    boundaries.push(...this.oneHopBoundaryNeighbors(boundaries, () => true));
    // 本方法只负责「本弧搭在一条**薄边界带**上」这一类接头：薄带(圆环带/薄条)由厚 0 边界围成，宽度是墙厚量级，
    // 其端头有一条很短的【封口线】。本弧从封口处搭出，端盖须由带的两侧边界 + 先画隔墙轮廓共同切出。
    // 判据 = 接头处(含一跳邻边)存在长度 ≤ 4×墙厚的短边界段(即封口线)。
    // 反例(须交回既有逻辑)：本弧端点贴在【房间外框】边界上(每条边长数千、无短封口段)——那类接头由 calArcJunctionCap /
    // 通用求交按 L 形贴墙端盖、斜接 miter 等既有规则处理(arcJunctionRoomWall / arcMixTangent / lastArcEnd 等用例)。
    const capLineLimit = mainWallInfo.thickness * 4;
    const hasThinBandCap = boundaries.some(
      (b) => !b.center && Point2dUtils.distanceTo(b.seg.start, b.seg.end) <= capLineLimit
    );
    if (!hasThinBandCap) return null;
    // 本弧须是接头处「最后建立」的有厚隔墙(后画被先画切):若接头还连着比本弧更晚建立的隔墙,本弧不是最后一条,
    // 不该被此方法整体切平(应保留与相邻墙的斜接,让真正最后那条被切)。与 clipStraightThroughBand/calArcJunctionCap 一致。
    if (infos.some((w) => w.thickness > 0 && w.id !== mainWallInfo.id && this.orderOf(w.id) > mainOrder)) return null;
    // 该接头处「更早建立」的有厚隔墙(后画被先画切)。与本弧共用【两个】端点的孪生隔墙(弦/透镜/穹顶配置)不算——它是本弧
    // 的直径弦、鼓向接头另一侧,应由孪生近边/miter 逻辑处理,当 cutter 会把端盖拉到弦另一端(与直墙版 shareBothEndpoints 排除一致)。
    const earlier = infos.filter(
      (w) =>
        w.thickness > 0 &&
        w.id !== mainWallInfo.id &&
        this.orderOf(w.id) < mainOrder &&
        !sharesBothEndpoints(w.seg, mainWallInfo.seg)
    );
    if (!earlier.length) return null;

    const junction = endOf(mainWallInfo.seg, type);
    // 本弧在接头处沿 body 前进的切线方向(bodyDirectionOf 按 start/end 翻转)。不能用「接头→另一端点」的弦方向
    // 来定向——半圆(扫角=π)时弦与切向垂直、判据退化，扫角>π 时弦方向与切向相反，会把「反向相切」误判成
    // 「平滑续接」而放弃切割。
    const tangentAtJ = bodyDirectionOf(mainWallInfo.seg, type);
    // 与更早隔墙【同向】相切时，本弧是那条墙的平滑续接(顺接延伸)→ 交回既有相切/平行对接逻辑，不在此切。
    // 但【反向】相切(本弧从接头朝该墙的反侧走，如先画直墙向上、本弧从同一点向下鼓)并不是续接：两墙只在接头相触、
    // 各自朝相反方向延伸，本弧仍须被墙边界/先画轮廓正常切割。故相切判据要带方向，不能一律 return null。
    const smoothContinuation = earlier.some((w) => {
      if (!isTangentShape(mainWallInfo.shape, w.shape)) return false;
      const far = Point2dUtils.equals(w.seg.start, junction) ? w.seg.end : w.seg.start;
      const wallDir = Point2dUtils.getUnitDirection(junction, far);
      return tangentAtJ.x * wallDir.x + tangentAtJ.y * wallDir.y > 0; // 同向 → 平滑续接
    });
    if (smoothContinuation) return null;
    // 用户语义的「两次切割」：本弧的每条偏移圆，都被「厚 0 墙边界线」和「先画隔墙的轮廓边」共同裁——取其中【离接头最近
    // 的那个界内交点】。取最近而非最深，是因为端盖应止于本弧第一次穿出这些轮廓的地方(再往前已在先画墙/边界之外)。
    // 与直墙版 calStraightMultiCutByEarlier 等价：直墙那边由 calPointWallIntersection 的 PathFinder 沿并集轮廓拼出
    // 「自身偏移端点 → 先画墙偏移端点 → 沿先画墙偏移边往下的交点」；弧墙这边 PathFinder 会沿先画墙两条长偏移边绕到墙的
    // 另一头(端盖拖到数十倍墙厚外、且横跨先画墙整个宽度)，故改为直接按几何求这两刀。
    // 切割边(只取与本弧【同侧】的先画墙偏移边——本弧只在先画墙中线的一侧，不该跨到反侧把另一半盖住，与直墙版一致)：
    const sideOfEarlier = (w: IWallInfo, p: IPoint2d) =>
      Math.sign(
        (w.seg.end.x - w.seg.start.x) * (p.y - w.seg.start.y) - (w.seg.end.y - w.seg.start.y) * (p.x - w.seg.start.x)
      );
    const bulge = mainWallInfo.seg.getMidPoint();
    // 切割段分两级，对应用户语义的两刀，且【先画隔墙轮廓优先于墙边界】：
    //   ① sepSegs = 先画隔墙里与本弧同侧的那条偏移边(直墙为线段、弧墙为弧段) + 它在接头处的墙厚端盖面。
    //      这是「用先绘制的轮廓再切一次」那刀。本弧真穿进先画墙时，切点会在该边上离接头较深处(如 205≈3×墙厚)。
    //   ② bdSegs = 厚 0 墙边界段。这是「被墙体边界切」那刀。
    // 必须分级、不能混在一起取全局最近：边界线切点总在接头附近(≈半墙厚)，混取时会永远盖过先画墙轮廓上那个较深的真切点，
    // 于是第二刀被吞掉、退化成只被边界平切(用户反馈的「没去做切割」)。故先在 sepSegs 上找段内切点，找不到才退回 bdSegs。
    const sepSegs: IShapeSeg2d[] = [];
    for (const w of earlier) {
      const sb = sideOfEarlier(w, bulge);
      // 先画墙是【弧】时不能用这个「同侧」筛：sideOfEarlier 拿的是弧的弦，而弧的两条偏移边都在弦的鼓出侧(同侧)，
      // 筛不出内外之分；本弧的两条偏移端点还常常同时埋在先画弧的带内(两弧在接头近乎顺接时)，本来就要分别被内、外边裁。
      // 若照直墙那样按弦侧筛，先画弧鼓向本弧反侧时两条偏移边会被全数剔除 → sepSegs 只剩端盖面 → 那一刀整个丢掉(不裁先画弧)。
      // 弧 cutter 的内外之分由下面 cutOn 的「前进方向 + 段内 + 取最近」自然决定(反向的交点会被扫角判据滤掉)。
      const earlierIsArc = w.seg instanceof ArcSeg2d;
      for (const off of [w.leftSeg, w.rightSeg]) {
        if (earlierIsArc) {
          sepSegs.push(off);
          continue;
        }
        // 直墙先画墙：偏移边中点定该边在中线的哪一侧；同侧才作切割边(本弧不跨到先画墙反侧)。
        const om = { x: (off.start.x + off.end.x) / 2, y: (off.start.y + off.end.y) / 2 };
        if (sb !== 0 && sideOfEarlier(w, om) === sb) sepSegs.push(off);
      }
      // 先画墙在接头处的墙厚端盖面(端点相连时它对着本弧的那一面)。
      const atStart = Point2dUtils.equals(w.seg.start, junction);
      const atEnd = Point2dUtils.equals(w.seg.end, junction);
      if (atStart || atEnd) {
        const l = atStart ? w.leftSeg.start : w.leftSeg.end;
        const r = atStart ? w.rightSeg.start : w.rightSeg.end;
        sepSegs.push(new LineSeg2d(l, r));
      }
    }
    const bdSegs: IShapeSeg2d[] = boundaries.map((b) => b.seg);

    // 本弧某条偏移圆 ∩ 各切割段，取离接头最近的【严格界内】交点。
    // 段内判定必须严格(不给 t 留 >1 的容差)：内偏移与先画墙同侧偏移边的交点常落在该边端点之【外】一点点(t≈1.002)——
    // 那说明内偏移并没有真穿进先画墙(它只是掠过端点)，该点在墙边界之外(y>边界线)，若因容差被采纳就会把端盖顶出边界。
    // 排除后内偏移自然落到边界线上的切点，外偏移则取它真正穿进先画墙、被同侧偏移边裁下去的段内交点——即两次切割。
    const maxCut = mainWallInfo.thickness * 6;
    // 【前进方向过滤】切点必须落在本偏移弧「自接头沿 body 前进」的那一段上。本弧的偏移圆是整圆，与切割边往往在接头
    // 两侧各有一个交点；反向那个(本弧根本没走到的地方)常常离接头更近，若不过滤就会被「取最近」选中——本例内偏移在
    // 直墙左偏移边上的反向交点 dJ=35 会盖过正向的 dJ=61(环带外弧上的真切点)，端盖朝弧的反向削出一条长线。
    // 判据用本偏移弧【自身的扫角】(与 calArcJunctionCap 的 crossSeg 一致)，而不是"相对接头的方向点积"——后者在
    // 交点几乎垂直于切线时(如上述 dJ=35 的点 dot≈-0.005)无法区分正反。
    // 同时要求扫过的弧长 > 一个小量：扫角≈0 的交点其实就是本偏移弧的端点自身(相切掠过、并没有被切到)，
    // 例如外偏移与先画墙接头端盖面的交点就落在自己的偏移端点上，若采纳则端盖原地不动、真正的切割被跳过。
    const minCutArc = mainWallInfo.thickness * 0.02;
    const forwardSweep = (offsetArc: ArcSeg2d, p: IPoint2d): number | null => {
      const total = offsetArc.getSweepAngle();
      const sFromStart = offsetArc.getPointSweepAngle(p);
      let sweep = type === HandleType.Start ? sFromStart : total - sFromStart;
      if (sweep > Math.PI) sweep -= 2 * Math.PI; // 起点略后侧的回卷 → 小负值
      if (sweep * offsetArc.radius < minCutArc) return null; // 反向 / 就在端点上(未被切到)
      if (sweep > total + arcThresholdValue) return null; // 超出本弧段
      return sweep;
    };
    // 返回切点【及其所在的那条切割边】——转角必须由两个切点各自所在的边求交得到(见下方 cornerOf)。
    // limitReach：是否对切点施加「离接头 ≤6×墙厚」的局部闸。
    //   · 厚 0 边界(bdSegs)施加：边界弧常绕很远，需要闸挡住远端幻影。
    //   · 先画隔墙轮廓(sepSegs)【不施加】：本弧与先画墙近相切、同向时，重叠段本来就长(本例弧要走 567≈8×墙厚才脱离
    //     直墙带)，那才是真切点；此时「段内 + 前进方向 + 弧长>min」已足够严格——反向的假点(如 y=-3804)正是靠
    //     「不在该偏移边线段范围内」被排除的，不需要距离闸。加了闸反而把唯一的真切点滤掉、退化成不让开先画墙。
    const cutOn = (
      offsetArc: ArcSeg2d,
      cutSegs: IShapeSeg2d[],
      limitReach = true
    ): { point: IShapePoint2d; seg: IShapeSeg2d } | null => {
      const offset = offsetArc.createCircle();
      let best: { point: IPoint2d; seg: IShapeSeg2d } | null = null;
      for (const seg of cutSegs) {
        const ips = intersectCommonShape(offset, extendSeg(seg), arcThresholdValue);
        if (!ips) continue;
        for (const p of ips) {
          if (forwardSweep(offsetArc, p) === null) continue; // 不在本弧前进段上 → 不是切点
          // 交点须严格落在切割段的实际范围内(线段按投影 t、弧段按扫角)，否则是延长线/延长弧上的点。
          if (seg instanceof ArcSeg2d) {
            let s = seg.getPointSweepAngle(p);
            if (s > 2 * Math.PI - arcThresholdValue) s -= 2 * Math.PI;
            if (s < 0 || s > seg.getSweepAngle()) continue;
          } else {
            const dx = seg.end.x - seg.start.x;
            const dy = seg.end.y - seg.start.y;
            const l2 = dx * dx + dy * dy || 1;
            const t = ((p.x - seg.start.x) * dx + (p.y - seg.start.y) * dy) / l2;
            if (t < 0 || t > 1) continue;
          }
          const d = Point2dUtils.distanceTo(p, junction);
          if ((!limitReach || d <= maxCut) && (!best || d < Point2dUtils.distanceTo(best.point, junction)))
            best = { point: p, seg };
        }
      }
      return best ? { point: { x: best.point.x, y: best.point.y }, seg: best.seg } : null;
    };
    // 每条偏移弧：先看先画隔墙轮廓那刀(优先)，没切到才退回墙边界那刀。
    const cutOffset = (offsetArc: ArcSeg2d) => cutOn(offsetArc, sepSegs, false) ?? cutOn(offsetArc, bdSegs, true);
    if (!(mainWallInfo.leftSeg instanceof ArcSeg2d) || !(mainWallInfo.rightSeg instanceof ArcSeg2d)) return null;
    const lHit = cutOffset(mainWallInfo.leftSeg);
    const rHit = cutOffset(mainWallInfo.rightSeg);
    const lCut = lHit?.point ?? null;
    const rCut = rHit?.point ?? null;
    // 两条偏移都被切到 → 端盖为 [左切点, (转角), 右切点]。两切点分属不同切割边(一条在边界线、一条在先画墙偏移边)时，
    // 中间需插入这两条边的【交点】作转角，端盖才沿边界走到先画墙的轮廓角、再沿其偏移边下行(与直墙版经先画墙偏移端点同形)；
    // 若两切点与该转角近共线则退化为 2 点平口。转角须在局部尺度内(≤6×墙厚)，否则宁可两点直连不追远交点。
    if (lCut && rCut && lHit && rHit) {
      // 转角 = 【两个切点各自所在的那两条切割边】的交点(与 calArcJunctionCap 用 leftDeep.seg / rightDeep.seg 求交同理)。
      // 关键：不能遍历所有 sepSegs × bdSegs 取"离接头最近"——先画墙在接头的墙厚端盖面与边界线的交点就是接头 J 本身
      // (距离 0，永远最近)，会把真正的转角(边界线 ∩ 先画墙同侧偏移边，离接头约半墙厚)挤掉，端盖于是沿边界线折回 J、
      // 在靠近墙体边界点处切错(伸进先画墙 body 中段)。只用两个切点实际所在的边求交，转角才落在先画墙的轮廓角上。
      const cornerOf = (): IShapePoint2d | null => {
        if (lHit.seg === rHit.seg) return null; // 两侧切在同一条边 → 平口，无需转角
        const ips = intersectCommonShape(extendSeg(lHit.seg), extendSeg(rHit.seg), arcThresholdValue);
        if (!ips || !ips.length) return null;
        const near = ips.filter((p) => Point2dUtils.distanceTo(p, junction) <= maxCut);
        if (!near.length) return null;
        const q = getNearestPoint(near, junction);
        if (!q) return null;
        return { x: q.x, y: q.y };
      };
      // 交点是否落在这条切割边的【实际范围内】(线段按投影 t、弧段按扫角)。容差按【绝对越界距离】给(0.1×墙厚)，
      // 不是相对 t：先画墙的偏移边端点已被边界裁得几乎贴着边界线时，交点只越界零点几毫米——那实质就是该边的端点、
      // 仍是真实拐角；越界几十毫米(先画墙端盖面与边界线成大角、两者之间隔着一整个墙厚面)才是「延长线上的假拐角」。
      const overshootTol = mainWallInfo.thickness * 0.1;
      const withinSeg = (s: IShapeSeg2d, p: IPoint2d) => {
        if (s instanceof ArcSeg2d) return sweepOnArc(s, p, overshootTol / (s.radius || 1)) !== null;
        const dx = s.end.x - s.start.x;
        const dy = s.end.y - s.start.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const t = ((p.x - s.start.x) * dx + (p.y - s.start.y) * dy) / (len * len);
        const tol = overshootTol / len;
        return t >= -tol && t <= 1 + tol;
      };
      const corner = cornerOf();
      // 两条切割边【在各自的实际范围内并不相接】(延长线才交、或压根不相交)：真实轮廓在这里不是一个拐角——两条边之间
      // 还夹着先画墙的墙厚端盖面与边界带的封口线。此时端盖须绕经【各自靠接头的那个端点】(先画墙在 J 的偏移端点 /
      // 边界带的角点)并经接头 J 折过去，成阶梯折线；否则两切点直连会斜跨过接头附近这块——要么把端盖顶到两条边之外
      // 与先画墙之间留出镂空，要么反过来盖住先画墙的端盖角(都是用户反馈的「切割处不贴合」)。
      if (!corner || !(withinSeg(lHit.seg, corner) && withinSeg(rHit.seg, corner))) {
        const nearEndOf = (s: IShapeSeg2d) => getNearestPoint([s.start, s.end], junction);
        const stepped: IShapePoint2d[] = [lCut];
        pushCapPoint(stepped, nearEndOf(lHit.seg), junction, maxCut);
        pushCapPoint(stepped, junction, junction, maxCut);
        pushCapPoint(stepped, nearEndOf(rHit.seg), junction, maxCut);
        pushCapPoint(stepped, rCut, junction, maxCut);
        if (stepped.length >= 3 && Point2dUtils.distanceTo(stepped[stepped.length - 1], rCut) <= thresholdValue) {
          return markCapArcs(stepped, mainWallInfo, lHit.seg, rHit.seg);
        }
        return markCapArcs([lCut, rCut], mainWallInfo, lHit.seg, rHit.seg);
      }
      // 只要转角不与两切点重合就保留它：它是「边界线 → 先画墙偏移边」的真实轮廓拐角，端盖须经它折一下，
      // 才是沿两条轮廓贴合切出的形状(与直墙版经先画墙偏移端点的 3 点折线同构)。
      // 注意不能按「近共线」丢弃：本例转角离两切点连线只偏约 1mm(远小于墙厚的 5%)，但它正是靠近墙体边界点处
      // 那个拐点——丢掉它端盖就成一条直斜线、在边界点附近切错(用户反馈)。共线容差只用几何精度级别。
      const dl = Point2dUtils.distanceTo(corner, lCut);
      const dr = Point2dUtils.distanceTo(corner, rCut);
      return markCapArcs(
        dl > thresholdValue && dr > thresholdValue ? [lCut, corner, rCut] : [lCut, rCut],
        mainWallInfo,
        lHit.seg,
        rHit.seg
      );
    }

    // 恰有一侧被切、另一侧自由：本弧只有一半埋在边界带/先画墙轮廓里，另一半从接头处就露在外面。
    // 典型：薄环带只覆盖接头的一侧(如右半圆环带只在 x≥封口线处有弧)，本弧向带外斜出——带内那条偏移被带的边裁回，
    // 带外那条偏移不被任何轮廓切、保留自身偏移端点。
    // 端盖须经【被切侧那条切割边靠接头的端点】(即边界带的角点，如封口线下端 = 外弧终点)折一下，再连到自由侧端点；
    // 否则两点直连会斜跨过带的角、把带口盖住。
    // 两侧都被切时置空，因此 hit 非空即等价于原来的「恰有一侧被切」判据，同时直接拿到被切的那一侧
    const hit = lHit && rHit ? null : (lHit ?? rHit);
    if (hit) {
      const lEnd = endOf(mainWallInfo.leftSeg, type);
      const rEnd = endOf(mainWallInfo.rightSeg, type);
      const lPoint: IShapePoint2d = lHit ? lHit.point : { x: lEnd.x, y: lEnd.y };
      const rPoint: IShapePoint2d = rHit ? rHit.point : { x: rEnd.x, y: rEnd.y };
      // 被切侧切割边靠接头的那个端点 = 边界带的角点。须在局部尺度内，且不与两端点重合才插入。
      const ends = [hit.seg.start, hit.seg.end];
      const nearEnd = getNearestPoint(ends, junction);
      const corner =
        nearEnd && Point2dUtils.distanceTo(nearEnd, junction) <= maxCut
          ? ({ x: nearEnd.x, y: nearEnd.y } as IShapePoint2d)
          : null;
      // 转角须与两端点都有实质距离才插入：本弧与先画墙近相切时，「被切边靠接头的端点」会几乎落在本弧的自由侧偏移
      // 端点上(本例相距 0.35)，插入它(及 J)会让端盖绕出一个 35 的无意义折角。此时端盖就是两端点直连(沿先画墙的偏移边)。
      if (
        corner &&
        Point2dUtils.distanceTo(corner, lPoint) > minCutArc &&
        Point2dUtils.distanceTo(corner, rPoint) > minCutArc
      ) {
        // 端盖须【经接头点 J】再连到自由侧，共 4 点：
        //   被切侧切点 →(沿被切边)→ 边界带角点 →(沿带的封口线)→ 接头 J →(沿墙厚方向)→ 自由侧偏移端点。
        // 若直接从角点连到自由侧端点，会得到一条不对应任何边界的【斜边】(本例 Δ(-35,+49.7))——而这一带的边界
        // 全是封口线(竖直)与环带弧(近水平)，本无斜边。插入 J 后端盖沿"角点→J"走封口线、"J→自由端"走墙厚面，
        // 成阶梯折线、每段都贴合真实边界。角点、J、自由端近共线时不插 J(退回 3 点)。
        const jp: IShapePoint2d = { x: junction.x, y: junction.y };
        const freePoint = lHit ? rPoint : lPoint;
        const cr = (freePoint.x - corner.x) * (jp.y - corner.y) - (freePoint.y - corner.y) * (jp.x - corner.x);
        const bs = Point2dUtils.distanceTo(corner, freePoint) || 1;
        const needJ =
          Math.abs(cr) / bs > thresholdValue &&
          Point2dUtils.distanceTo(jp, corner) > thresholdValue &&
          Point2dUtils.distanceTo(jp, freePoint) > thresholdValue;
        if (!needJ) return [lPoint, corner, rPoint];
        // corner 在被切侧：lHit 被切 → corner 紧跟 lPoint；rHit 被切 → corner 紧邻 rPoint(J 在前)。
        return lHit ? [lPoint, corner, jp, rPoint] : [lPoint, jp, corner, rPoint];
      }
      return [lPoint, rPoint];
    }

    // 只有一条(或都没)偏移被切到 → 退回通用并集端盖，再做绕远/跨侧收尾修正(保守，避免半个端盖)。
    const cutters = [...earlier, ...boundaries];
    const cap = this.calPointWallIntersection(cutters, mainWallInfo, type, true);
    if (!cap || cap.length < 2) return null;

    // 与直墙版同样的收尾修正：某条偏移边与它「本该切到的那条更早墙」近平行/在弧上绕远时，PathFinder 会沿更早墙的整条
    // body 绕到墙的另一头(端盖被拖到离接头数十倍墙厚处、轮廓自交)。对离接头过远(>6×墙厚)的那个端点，改用**连接处的
    // 墙厚端盖面**(更早墙在 J 的两条偏移端点连线)把本弧这条偏移圆平切过来，端盖与先画墙在接头处的轮廓面齐平。
    // 阈值与直墙版一致(maxReach=6×墙厚，合法端盖点实测 ≤1×墙厚)。
    const maxReach = mainWallInfo.thickness * 6;
    // 用更早墙在接头处的墙厚端盖面(或边界线)把本弧这条偏移圆平切过来，取离接头最近的交点。
    // body 搭接(非端点相连)的更早墙没有接头端盖面，跳过。
    const cutAcross = (offset: Circle2d, fallback: IPoint2d): IShapePoint2d => {
      const cands = [
        ...earlier
          .filter((w) => isEndpointOf(w.seg, junction))
          .flatMap((w) => intersectCommonShape(offset, endCapFaceAt(w, junction)) || []),
        ...boundaries.flatMap(
          (b) => intersectCommonShape(offset, new Line2d({ points: { p1: b.seg.start, p2: b.seg.end } })) || []
        )
      ];
      return nearestWithinReach(cands, junction, maxReach, fallback);
    };
    const leftOrigin = endOf(mainWallInfo.leftSeg, type);
    const rightOrigin = endOf(mainWallInfo.rightSeg, type);
    const out = cap.slice();
    // 端盖左→右排列：out[0] 对应 leftShape 偏移圆、out[末] 对应 rightShape 偏移圆(getWallDrawShape 约定)。
    // 两侧端点若被拖远(直墙版的典型退化)→ 用端盖面平切过来。
    if (Point2dUtils.distanceTo(out[0], junction) > maxReach) {
      out[0] = cutAcross(mainWallInfo.leftShape, leftOrigin);
    }
    if (Point2dUtils.distanceTo(out[out.length - 1], junction) > maxReach) {
      out[out.length - 1] = cutAcross(mainWallInfo.rightShape, rightOrigin);
    }
    // 弧墙的退化形态与直墙版不同：两侧切点本就贴着接头(在 maxReach 内)，绕远的是【中间过渡点】——PathFinder 沿先画墙
    // 的两条长偏移边绕到墙的另一头再折回(如拖到 y≈-2656、离接头 2900≈41×墙厚)。这些中间点须丢弃。
    // 同时：本弧只位于先画墙中线的【一侧】，端盖不该跨到反侧去(跨过去会横切先画墙的整个宽度、把本不属于本弧的那半盖住)。
    // 与直墙版一致——它也只贴「本墙所在那一侧」的偏移边切。故中间点还须与本弧同侧(复用上方 sideOfEarlier / bulge)。
    // 中间过渡点是否落在某条更早墙中线的「本弧反侧」(跨过去了) → 丢弃。恰在中线上(0)不算跨。
    const onFarSide = (p: IPoint2d) =>
      earlier.some((w) => {
        const sp = sideOfEarlier(w, p);
        const sb = sideOfEarlier(w, bulge);
        return sp !== 0 && sb !== 0 && sp !== sb;
      });
    // 抹掉残留的 center/clockwise：端盖是直线段，不应带绕行弧信息。
    const cleaned: IShapePoint2d[] = [{ x: out[0].x, y: out[0].y }];
    for (let i = 1; i < out.length - 1; i++) {
      if (Point2dUtils.distanceTo(out[i], junction) > maxReach) continue; // 绕远的过渡点 → 丢弃
      if (onFarSide(out[i])) continue; // 跨到先画墙反侧的点 → 丢弃
      cleaned.push({ x: out[i].x, y: out[i].y });
    }
    cleaned.push({ x: out[out.length - 1].x, y: out[out.length - 1].y });
    return cleaned.length >= 2 ? cleaned : null;
  }

  /**
   * 端盖被「更早那条墙的近边」裁回的公共改写(近边是直线段或圆，由三个回调描述)：
   *   · 首点对应 leftShape 偏移圆、末点对应 rightShape 偏移圆(与 getWallDrawShape 约定一致)，裁到「偏移圆 ∩ 近边」；
   *   · 中间越界点(如两弧相切接头的中线点)取其在近边上的垂足/径向投影，保留角平分线三点端盖形态；
   *   · 落在近边外侧(弧鼓起一侧)的点保持不变。
   * 未发生裁剪、或裁剩不足以成盖时返回 null，由调用方保持原端盖。
   */
  private clipCapAgainstNearEdge(
    mainWallInfo: IWallInfo,
    cap: IShapePoint2d[],
    intrudes: (p: IPoint2d) => boolean,
    clipTo: (p: IPoint2d, offsetCircle: Circle2d) => IShapePoint2d | null,
    footOnNear: (p: IPoint2d) => IShapePoint2d
  ): IShapePoint2d[] | null {
    let changed = false;
    const result: IShapePoint2d[] = [];
    for (let i = 0; i < cap.length; i++) {
      const p = cap[i];
      if (!intrudes(p)) {
        result.push(p);
        continue;
      }
      changed = true;
      if (i === 0 || i === cap.length - 1) {
        const offsetCircle = (i === 0 ? mainWallInfo.leftShape : mainWallInfo.rightShape) as Circle2d;
        const q = clipTo(p, offsetCircle);
        if (q) result.push(q);
      } else {
        result.push(footOnNear(p));
      }
    }
    return changed && result.length >= 2 ? result : null;
  }

  // 两条弧形隔墙共点相接、且该接头端点落在「更早建立的直隔墙 body」上：两弧仍在上方相交(顶部 miter 保留)，
  // 但伸进直墙带内的下部端盖点应被直墙(先建)沿其近边裁掉——先绘制的直墙把两弧交点处的下部盖住。
  // 做法：把已算好的两弧接头端盖里、越过直墙近边(落到直墙内侧/中线侧)的端盖点，裁到「该端盖点所在偏移圆 ∩ 直墙近边线」上；
  // 落在近边外侧(弧鼓起一侧，如顶部 miter)的点保留，中间越界点(接头中线点)丢弃。仅当弧接头端点确落在更早直隔墙 body 上时生效。
  private clipArcCapByEarlierStraightBody(
    mainWallInfo: IWallInfo,
    type: HandleType,
    cap: IShapePoint2d[]
  ): IShapePoint2d[] | null {
    if (!(mainWallInfo.seg instanceof ArcSeg2d)) return null;
    if (!(mainWallInfo.leftShape instanceof Circle2d) || !(mainWallInfo.rightShape instanceof Circle2d)) return null;
    const onWall = onWallOf(mainWallInfo, type);
    const mainOrder = this.orderOf(mainWallInfo.id);
    // 弧接头端点落在其 body 上的、更早建立的直隔墙(厚>0、非圆)。
    const straight = onWall
      .map((w) => this._cacheWallMap.get(w.id))
      .find(
        (w): w is IWallInfo =>
          !!w && w.thickness > 0 && !(w.shape instanceof Circle2d) && this.orderOf(w.id) < mainOrder
      );
    if (!straight) return null;

    // 弧鼓点(中点)：用于确定弧体在直墙中心线的哪一侧 → 该侧的偏移边即近边。
    const bulge = mainWallInfo.seg.getMidPoint();
    const centerSide = Math.sign(crossOf(straight.seg.start, straight.seg.end, bulge));
    const leftEdge = straight.leftSeg;
    const rightEdge = straight.rightSeg;
    const nearEdge =
      Math.sign(crossOf(straight.seg.start, straight.seg.end, leftEdge.start)) === centerSide ? leftEdge : rightEdge;
    const nearLine = new Line2d({ points: { p1: nearEdge.start, p2: nearEdge.end } });
    const bulgeSideOfNear = Math.sign(crossOf(nearEdge.start, nearEdge.end, bulge));
    // 端盖点是否越过近边落到直墙内侧(与弧鼓点相反侧)。近边线上/鼓侧的点不裁。
    const intrudes = (p: IPoint2d) => {
      const s = Math.sign(crossOf(nearEdge.start, nearEdge.end, p));
      return s !== 0 && s !== bulgeSideOfNear;
    };
    const clipTo = (p: IPoint2d, circle: Circle2d): IShapePoint2d | null => {
      const ips = intersectCommonShape(circle, nearLine, arcThresholdValue);
      if (!ips || !ips.length) return null;
      const q = getNearestPoint(ips, p);
      if (!q) return null;
      return { x: q.x, y: q.y };
    };

    // 点在近边线上的垂足(中间越界点如两弧相切接头的中线点用它裁到近边，保留角平分线三点端盖形态)。
    const footOnNear = (p: IPoint2d): IShapePoint2d => {
      const ax = nearEdge.start.x;
      const ay = nearEdge.start.y;
      const ux = nearEdge.end.x - ax;
      const uy = nearEdge.end.y - ay;
      const len2 = ux * ux + uy * uy;
      if (len2 < thresholdValue) return { x: p.x, y: p.y };
      const t = ((p.x - ax) * ux + (p.y - ay) * uy) / len2;
      return { x: ax + t * ux, y: ay + t * uy };
    };

    return this.clipCapAgainstNearEdge(mainWallInfo, cap, intrudes, clipTo, footOnNear);
  }

  // 与 clipArcCapByEarlierStraightBody 对称：两条弧形隔墙共点相接、且该接头端点落在「更早建立的弧隔墙 body」上
  // (接头端点落在其中线圆的偏移带内)。先绘制的弧把接头处盖住，本弧(两条后建弧之一)伸进该弧带内的端盖点应被裁回其近偏移边。
  // 差异仅在于 body 为弧：近边是圆(更早弧的近侧偏移圆)而非直线，内外用「离更早弧圆心的半径」判定。
  // 做法：把伸进带内(被更早弧盖住)的端盖点，沿本弧对应偏移圆 ∩ 更早弧近偏移圆裁到边缘，两弧接头处齐平于更早弧的边。
  private clipArcCapByEarlierArcBody(
    mainWallInfo: IWallInfo,
    type: HandleType,
    cap: IShapePoint2d[]
  ): IShapePoint2d[] | null {
    if (!(mainWallInfo.seg instanceof ArcSeg2d)) return null;
    if (!(mainWallInfo.leftShape instanceof Circle2d) || !(mainWallInfo.rightShape instanceof Circle2d)) return null;
    const onWall = onWallOf(mainWallInfo, type);
    const mainOrder = this.orderOf(mainWallInfo.id);
    // 弧接头端点落在其 body 上的、更早建立的弧隔墙(厚>0、圆)。
    const body = onWall
      .map((w) => this._cacheWallMap.get(w.id))
      .find(
        (w): w is IWallInfo =>
          !!w && w.thickness > 0 && w.shape instanceof Circle2d && !!w.center && this.orderOf(w.id) < mainOrder
      );
    if (!body || !(body.shape instanceof Circle2d) || !body.center) return null;
    const bodyCenter = body.center;
    const bodyRadius = body.shape.radius; // 更早弧中线圆半径
    const radiusFrom = (p: IPoint2d) => Math.hypot(p.x - bodyCenter.x, p.y - bodyCenter.y);
    // 本弧鼓点(中点)在更早弧中线圆的内侧还是外侧 → 该侧的偏移圆即近边。
    const bulge = mainWallInfo.seg.getMidPoint();
    const bulgeSign = Math.sign(radiusFrom(bulge) - bodyRadius);
    if (bulgeSign === 0) return null;
    const leftOff = body.leftShape as Circle2d;
    const rightOff = body.rightShape as Circle2d;
    const nearCircle = Math.sign(leftOff.radius - bodyRadius) === bulgeSign ? leftOff : rightOff;
    const nearRadius = nearCircle.radius;
    // 端盖点是否越过近边落到更早弧带内(与本弧鼓点相反侧)。近边上/鼓侧的点不裁。
    const intrudes = (p: IPoint2d) => {
      const s = Math.sign(radiusFrom(p) - nearRadius);
      return s !== 0 && s !== bulgeSign;
    };
    const clipTo = (p: IPoint2d, circle: Circle2d): IShapePoint2d | null => {
      const ips = intersectCommonShape(circle, nearCircle, arcThresholdValue);
      if (!ips || !ips.length) return null;
      const q = getNearestPoint(ips, p);
      if (!q) return null;
      return { x: q.x, y: q.y };
    };
    // 中间越界点投影到近边圆上(沿更早弧圆心方向)，保留三点端盖形态。
    const footOnNear = (p: IPoint2d): IShapePoint2d => {
      const d = Point2dUtils.getUnitDirection(bodyCenter, p);
      return { x: bodyCenter.x + d.x * nearRadius, y: bodyCenter.y + d.y * nearRadius };
    };

    const result = this.clipCapAgainstNearEdge(mainWallInfo, cap, intrudes, clipTo, footOnNear);
    // 被裁到近边圆上的相邻两点之间，走的就是更早那条弧的偏移边(弧)，不是弦。
    return markCapArcs(result, mainWallInfo, nearCircle === leftOff ? body.leftSeg : body.rightSeg);
  }

  protected calMixConnected(mainWallInfo: IWallInfo, type: HandleType): IShapePoint2d[] | null {
    const mainPointPointConnected = connectToPointOf(mainWallInfo, type);

    const mainPointWallConnected = onWallOf(mainWallInfo, type);

    // 后建墙不应切先建墙：端点-端点相连墙里只保留「比本墙更早建立」的，参与切割。
    // 否则(如本墙是弧、其端点又被后建的直墙连上、且该直墙恰与弧相切)会把后建墙边线当切割边，端盖错误地延伸到后建墙上。
    // 建立先后必须用真实建立顺序(_buildOrder，来自 _wallInfos 下标)判定，不能用缓存插入顺序：
    // 全量计算时房间墙先入缓存会把相连墙按非建立顺序拉进缓存，用缓存顺序会把先建墙误判为「后建」，
    // 导致两条端点相连的隔墙互相错切(如 zigzag 链里后建的 wN 反而切了先建的 wN-1)。与 calArcJunctionCap 一致。
    const mainOrder = this.orderOf(mainWallInfo.id);
    const earlierPointPoint = mainPointPointConnected.filter((w) => this.orderOf(w.id) < mainOrder);

    // 弧形主墙端点与某侧墙相切：优先只用相切的那面侧墙算 L 形端盖(外弧裁到与侧墙的跨内交点)，
    // 不被同端多连的其它隔墙(如斜墙)带偏——否则弧端会对着那条墙裁、外弧仍戳出侧墙(右侧没切对)。
    // 与 calPointPointConnected 中的处理一致。
    if (mainWallInfo.center) {
      const tangentWalls = [...earlierPointPoint, ...mainPointWallConnected]
        .map((item) => this._cacheWallMap.get(item.id))
        .filter((w): w is IWallInfo => !!w && isTangentShape(mainWallInfo.shape, w.shape));
      if (tangentWalls.length) {
        const res = this.calPointWallIntersection(tangentWalls, mainWallInfo, type, true);
        if (res && res.length) return res;
      }
    }

    const handleWallInfos = [...earlierPointPoint, ...mainPointWallConnected]
      .map((item) => this._cacheWallMap.get(item.id))
      .filter((w) => w !== undefined);

    // 弧形主墙混合连接(端点-墙 + 端点-端点)时，同样用「本体同侧 + 跨角内」判据选交点(pointOnWall=true)，
    // 否则弧墙某一端因多连了一条墙走 dot 判据 → 与另一端不对称、外弧选不到跨内交点、端盖不裁(右侧戳出)。
    return this.calPointWallIntersection(handleWallInfos, mainWallInfo, type, !!mainWallInfo.center);
  }

  protected calLeftRightIntersection(
    mainWallInfo: IWallInfo,
    otherWallInfo: IWallInfo,
    mainConnectedPoint: IShapePoint2d,
    type: HandleType
  ): { mainLeft: IShapePoint2d | null; mainRight: IShapePoint2d | null; leftHit: boolean; rightHit: boolean } {
    const mainLeftLine = mainWallInfo.leftShape;
    const mainRightLine = mainWallInfo.rightShape;
    const mainLeftSeg = mainWallInfo.leftSeg;
    const mainRightSeg = mainWallInfo.rightSeg;

    const isStartEndConnected =
      Point2dUtils.equals(otherWallInfo.seg.start, mainWallInfo.seg.end) ||
      Point2dUtils.equals(otherWallInfo.seg.end, mainWallInfo.seg.start);

    // 左右配对：沿轮廓走向本墙的左侧接对方的哪一侧。共端点方向相反(共起点/共终点)时对方左右互换。
    const otherLeft = isStartEndConnected
      ? { shape: otherWallInfo.leftShape, seg: otherWallInfo.leftSeg }
      : { shape: otherWallInfo.rightShape, seg: otherWallInfo.rightSeg };
    const otherRight = isStartEndConnected
      ? { shape: otherWallInfo.rightShape, seg: otherWallInfo.rightSeg }
      : { shape: otherWallInfo.leftShape, seg: otherWallInfo.leftSeg };

    // 两墙从接头沿几乎相同方向离开(近发夹)：两条墙带从接头起大面积重叠、要到很远处才分开，斜接点距离
    // ≈ 墙厚/sin(夹角)，可以远远超过下面的 6×墙厚距离闸。此时若把它当远端幻影拦掉、两墙各自退回平口，
    // 除了接头露缝，后画那条墙的偏移边还会整段压在先画墙体内、直到「两带分离处」才穿出去 —— 轮廓交叉。
    // 所以近发夹时对【真交叉点】放开距离闸：真交叉 = 交点同时严格落在双方偏移段内(远端幻影都落在段外/弧扫角外)。
    // 取到它后两墙沿同一条缝(斜接顶点→接头)对接，重叠段归先画的墙，轮廓不再互相穿过。
    const nearHairpin = isNearHairpinJoint(mainWallInfo, otherWallInfo, mainConnectedPoint);

    // 斜接点必须落在接头附近：直墙与大弧近平行相接时，两条偏移边在远处(弧对侧)还会相交出一个「幻影」交点，
    // getNearestPoint 已取最近的，这里再加距离闸(≤6×墙厚)兜底，防止近平行时误取远端幻影把端盖拉飞。
    const maxMiter = mainWallInfo.thickness * 6;
    const pick = (
      main: { shape: IShape2d; seg: IShapeSeg2d },
      other: { shape: IShape2d; seg: IShapeSeg2d }
    ): { pt: IShapePoint2d; hit: boolean } => {
      const fallback = endOf(main.seg, type);
      const pts = intersectCommonShape(main.shape, other.shape);
      if (pts && pts.length) {
        const nearest = getNearestPoint(pts, mainConnectedPoint);
        if (nearest && Point2dUtils.distanceTo(nearest, mainConnectedPoint) <= maxMiter)
          return { pt: nearest, hit: true };
        if (nearHairpin) {
          const crossings = pts.filter((p) => strictlyOnSeg(main.seg, p) && strictlyOnSeg(other.seg, p));
          const nearestCrossing = crossings.length ? getNearestPoint(crossings, mainConnectedPoint) : null;
          if (nearestCrossing) return { pt: nearestCrossing, hit: true };
        }
      }
      return { pt: fallback, hit: false };
    };

    // 逐侧取斜接点：某侧偏移边能求到交点(且在接头附近)就取该侧斜接点，求不到(如直+弧的凹/凸侧偏移边够不到对方偏移弧)
    // 只回退【该侧】自身偏移端点，不能因为一侧无交点就把两侧整体退回平口——那会让本该角平分线斜拼的简单两墙相接
    // 变成平口对接留缝。仅一侧命中时由调用方经接头点连成 3 点折线补齐接头角(见 leftHit/rightHit)。
    const left = pick({ shape: mainLeftLine, seg: mainLeftSeg }, otherLeft);
    const right = pick({ shape: mainRightLine, seg: mainRightSeg }, otherRight);
    return { mainLeft: left.pt, mainRight: right.pt, leftHit: left.hit, rightHit: right.hit };
  }

  protected calLeftRightIntersectionWithRightAngle(
    mainId: string,
    otherId: string
  ): null | {
    mainLeft: IShapePoint2d | null;
    mainRight: IShapePoint2d | null;
    otherLeft: IShapePoint2d | null;
    otherRight: IShapePoint2d | null;
  } {
    const mainWallInfo = this._cacheWallMap.get(mainId);
    const otherWallInfo = this._cacheWallMap.get(otherId);
    if (!mainWallInfo || !otherWallInfo) return null;

    const mainSeg = mainWallInfo.seg;
    const otherSeg = otherWallInfo.seg;
    const mainStart = mainSeg.start;
    const mainEnd = mainSeg.end;
    const otherStart = otherSeg.start;
    const otherEnd = otherSeg.end;
    const mainLeftLine = mainWallInfo.leftShape;
    const mainRightLine = mainWallInfo.rightShape;
    const otherLeftLine = otherWallInfo.leftShape;
    const otherRightLine = otherWallInfo.rightShape;

    const mainLeftLeftPoints = intersectCommonShape(mainLeftLine, otherLeftLine);
    const mainRightRightPoints = intersectCommonShape(mainRightLine, otherRightLine);
    const mainLeftRightPoints = intersectCommonShape(mainLeftLine, otherRightLine);
    const mainRightLeftPoints = intersectCommonShape(mainRightLine, otherLeftLine);
    const mainLeftLeft = mainLeftLeftPoints ? getNearestPoint(mainLeftLeftPoints, mainStart) : null;
    const mainRightRight = mainRightRightPoints ? getNearestPoint(mainRightRightPoints, mainEnd) : null;
    const mainLeftRight = mainLeftRightPoints ? getNearestPoint(mainLeftRightPoints, mainEnd) : null;
    const mainRightLeft = mainRightLeftPoints ? getNearestPoint(mainRightLeftPoints, mainStart) : null;

    let isOtherOnMainLeft = false;
    {
      const otherMid = Point2dUtils.getCenter(otherStart, otherEnd);
      const startSide = mainSeg.pointSide(otherMid);
      if (startSide > 0) {
        isOtherOnMainLeft = true;
      } else if (startSide < 0) {
        isOtherOnMainLeft = false;
      } else {
        const endSide = mainSeg.pointSide(otherStart);
        if (endSide > 0) {
          isOtherOnMainLeft = true;
        } else if (endSide < 0) {
          isOtherOnMainLeft = false;
        }
      }
    }

    // 8种情况列举
    if (Point2dUtils.equals(mainStart, otherStart)) {
      if (isOtherOnMainLeft) {
        return {
          mainLeft: mainLeftLeft,
          mainRight: mainRightLeft,
          otherLeft: mainLeftLeft,
          otherRight: mainLeftRight
        };
      } else {
        return {
          mainLeft: mainLeftRight,
          mainRight: mainRightRight,
          otherLeft: mainRightLeft,
          otherRight: mainRightRight
        };
      }
    } else if (Point2dUtils.equals(mainStart, otherEnd)) {
      if (isOtherOnMainLeft) {
        return {
          mainLeft: mainLeftRight,
          mainRight: mainRightRight,
          otherLeft: mainLeftLeft,
          otherRight: mainLeftRight
        };
      } else {
        return {
          mainLeft: mainLeftLeft,
          mainRight: mainRightLeft,
          otherLeft: mainRightLeft,
          otherRight: mainRightRight
        };
      }
    } else if (Point2dUtils.equals(mainEnd, otherStart)) {
      if (isOtherOnMainLeft) {
        return {
          mainLeft: mainLeftRight,
          mainRight: mainRightRight,
          otherLeft: mainLeftLeft,
          otherRight: mainLeftRight
        };
      } else {
        return {
          mainLeft: mainLeftLeft,
          mainRight: mainRightLeft,
          otherLeft: mainRightLeft,
          otherRight: mainRightRight
        };
      }
    } else if (Point2dUtils.equals(mainEnd, otherEnd)) {
      if (isOtherOnMainLeft) {
        return {
          mainLeft: mainLeftLeft,
          mainRight: mainRightLeft,
          otherLeft: mainLeftLeft,
          otherRight: mainLeftRight
        };
      } else {
        return {
          mainLeft: mainLeftRight,
          mainRight: mainRightRight,
          otherLeft: mainRightLeft,
          otherRight: mainRightRight
        };
      }
    }

    return null;
  }

  protected calLeftRightIntersectionWithParallel(
    mainId: string,
    otherId: string,
    type: HandleType
  ): null | { mainLeftPoint: IShapePoint2d | null; mainRightPoint: IShapePoint2d | null } {
    const mainWallInfo = this._cacheWallMap.get(mainId);
    const otherWallInfo = this._cacheWallMap.get(otherId);
    if (!mainWallInfo || !otherWallInfo) return null;

    const mainSeg = mainWallInfo.seg;
    const otherSeg = otherWallInfo.seg;
    const mainConnectedPoint = endOf(mainWallInfo.seg, type);
    const otherConnectedPoint = Point2dUtils.equals(otherSeg.start, mainConnectedPoint) ? otherSeg.start : otherSeg.end;
    const midPoint = {
      x: (mainConnectedPoint.x + otherConnectedPoint.x) / 2,
      y: (mainConnectedPoint.y + otherConnectedPoint.y) / 2
    };
    const perpendicularSeg = mainSeg.getPerpendicularLine(midPoint);
    const perpendicularLine = new Line2d({
      points: { p1: perpendicularSeg.start, p2: perpendicularSeg.end }
    });
    const mainLeftLine = mainWallInfo.leftShape;
    const mainRightLine = mainWallInfo.rightShape;

    const leftPoints = intersectCommonShape(perpendicularLine, mainLeftLine);
    const rightPoints = intersectCommonShape(perpendicularLine, mainRightLine);

    return {
      mainLeftPoint: leftPoints ? getNearestPoint(leftPoints, mainConnectedPoint) : null,
      mainRightPoint: rightPoints ? getNearestPoint(rightPoints, mainConnectedPoint) : null
    };
  }

  protected setLocalWallShape(wallId: string, type: HandleType, points: IShapePoint2d[]) {
    const mainWallInfo = this._cacheWallMap.get(wallId);
    if (!mainWallInfo) return;

    if (type === HandleType.Start) {
      mainWallInfo.startShape = [...points];
    } else {
      mainWallInfo.endShape = [...points];
    }

    mainWallInfo.drawShape = getWallDrawShape(
      {
        start: mainWallInfo.start,
        end: mainWallInfo.end,
        center: mainWallInfo.center,
        clockwise: mainWallInfo.clockwise
      },
      mainWallInfo.startShape,
      mainWallInfo.endShape
    );
  }

  // 计算主墙体端点与多条墙体相连的情况
  protected calPointWallIntersection(
    subWallInfos: IWallInfo[],
    mainWallInfo: IWallInfo,
    type: HandleType,
    pointOnWall: boolean = false
  ): IShapePoint2d[] | null {
    const subWallShapeSegInfos = this.getShapeSegInfos(subWallInfos);
    const subWallShapeSegs = subWallShapeSegInfos.map(({ seg }) => seg);
    const mainLeftSeg = mainWallInfo.leftSeg;
    const mainRightSeg = mainWallInfo.rightSeg;

    // wallId → 相邻墙中线(Circle2d/Line2d)；主墙本体参考点 = 主墙中线中点。
    // 用于判断交点落在相邻墙的哪一侧，从而两侧都拼到主墙本体所在那条边。
    const subCenterMap = new Map<string, Circle2d | Line2d>();
    subWallInfos.forEach((w) => {
      if (w.shape instanceof Circle2d || w.shape instanceof Line2d) subCenterMap.set(w.id, w.shape);
    });
    const mainSegC = mainWallInfo.seg;
    const bodyRef =
      mainSegC instanceof ArcSeg2d
        ? mainSegC.getMidPoint()
        : { x: (mainSegC.start.x + mainSegC.end.x) / 2, y: (mainSegC.start.y + mainSegC.end.y) / 2 };

    // 房间/结构墙(厚 0)的中线作为边界：弧端盖交点不应落在房间墙外侧(与主墙本体 bodyRef 异侧)。
    // 接头处多墙共点会在弧上产生贴着接头的退化交点，其中戳出房间墙的那个会被选中导致端盖戳出；
    // 用边界把这些「室外」候选剔除，端盖就会落到房间墙边线上而非戳出。
    const boundaryLines = subWallInfos
      .filter((w) => w.thickness === 0 && w.shape instanceof Line2d)
      .map((w) => w.shape as Line2d);

    // 相连厚墙的实心带：合法端盖顶点不会埋在另一条墙体内部。接头处多墙共点时，弧上会出现一些贴着接头、
    // 却落在相邻墙(如斜墙 diag)实心带里的退化交点——剔除它们后，内侧边会切到真正相邻墙的「边线」上而非埋进墙里。
    const occluders = subWallInfos
      .filter((w) => w.thickness > 0)
      .map((w) => ({ seg: w.seg, halfWidth: w.thickness / 2 }));

    // 子边延长形态求交时允许的越界量：斜接角点最多落在子边端点外约一个墙厚处，取 2×墙厚留余量。
    // 必须是绝对长度而非子边长度的比例，否则长房间墙会把远处延长线上的幻影交点当成真交点(见函数内注释)。
    const maxOvershoot = Math.max(mainWallInfo.thickness, 1) * 2;
    // 厚 0 边界的子边不给越界余量：它没有端盖也没有斜接填充，线段之外没有材料(见 IExtendedSegIntersectSegsParams)。
    const exactSegWallIds = new Set(subWallInfos.filter((w) => w.thickness === 0).map((w) => w.id));

    // 计算交点
    let minLeftIndex = -1;
    let minRightIndex = -1;
    let mainLeftPoint;
    let mainRightPoint;
    let extremeShapePath: IShapePoint2d[] | null = null;

    // 本墙某条偏移边【在接头那头的端点】是否已经「埋在别的东西里」：
    //   · 严格落在某条相连厚墙的实心带内(strict：不含方形端帽外扩，见 isStrictlyInsideBand)；或
    //   · 已经戳过某条厚 0 边界(与本墙本体分居该边界两侧、且落在该边界的实际范围内) —— 即落进边界围出的实心区。
    // 埋着 → 该侧端盖点只能取沿 body 前向的出口(见 extendedLineIntersectSegments 里的 originEnclosed)。
    // 两类材料必须一视同仁：薄片/弧带(厚 0 边界围出的实心区)与厚隔墙对端盖的约束是同一回事，
    // 只按 occluders(厚墙)判会漏掉「偏移端点埋在薄片里」的那一半情形。
    const otherEndPt = farEndOf(mainWallInfo.seg, type);
    const boundaryInfos = subWallInfos.filter((w) => w.thickness === 0);
    const pokedThroughBoundary = (o: IPoint2d, w: IWallInfo): boolean => {
      if (w.seg instanceof ArcSeg2d) {
        // 弧边界：本墙本体与该点分居弧圆内外两侧，且该点落在弧的扫角内。
        const arc = w.seg;
        const dOther = Point2dUtils.distanceTo(otherEndPt, arc.center) - arc.radius;
        const dPoint = Point2dUtils.distanceTo(o, arc.center) - arc.radius;
        if (Math.abs(dPoint) <= thresholdValue || dPoint * dOther >= 0) return false;
        return sweepOnArc(arc, o) !== null;
      }
      const a = w.seg.start;
      const b = w.seg.end;
      const len = Point2dUtils.distanceTo(a, b);
      if (len <= thresholdValue) return false;
      const cross = (p: IPoint2d) => (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
      const sP = cross(o);
      if (Math.abs(sP) / len <= thresholdValue || sP * cross(otherEndPt) >= 0) return false;
      const t = ((o.x - a.x) * (b.x - a.x) + (o.y - a.y) * (b.y - a.y)) / (len * len);
      return t >= 0 && t <= 1; // 投影须落在该边界【线段】范围内(不能用无限直线)
    };
    const originEnclosedAt = (o: IPoint2d) =>
      occluders.some((oc) => isStrictlyInsideBand(o, oc.seg, oc.halfWidth)) ||
      boundaryInfos.some((w) => pokedThroughBoundary(o, w));
    const leftOriginPt = endOf(mainLeftSeg, type);
    const rightOriginPt = endOf(mainRightSeg, type);

    // 延长线相交检测
    const leftIntersect = extendedSegIntersectSegs({
      mainSeg: mainLeftSeg,
      subSegInfos: subWallShapeSegInfos,
      handleType: type,
      isLeft: true,
      subCenterMap,
      bodyRef,
      pointOnWall,
      boundaryLines,
      occluders,
      maxOvershoot,
      exactSegWallIds,
      originEnclosed: originEnclosedAt(leftOriginPt)
    });
    const rightIntersect = extendedSegIntersectSegs({
      mainSeg: mainRightSeg,
      subSegInfos: subWallShapeSegInfos,
      handleType: type,
      isLeft: false,
      subCenterMap,
      bodyRef,
      pointOnWall,
      boundaryLines,
      occluders,
      maxOvershoot,
      exactSegWallIds,
      originEnclosed: originEnclosedAt(rightOriginPt)
    });

    if (leftIntersect) {
      mainLeftPoint = leftIntersect.point;
      minLeftIndex = leftIntersect.index;
    }
    if (rightIntersect) {
      mainRightPoint = rightIntersect.point;
      minRightIndex = rightIntersect.index;
    }
    // 两边都有交点，且交点落在同一条边上，则直接返回两点(单边平切)。
    // 弧墙也一样：两侧切在同一条相邻墙边上 → 两点；切在不同墙上 → 下面走 PathFinder 拼复合端盖。
    if (mainLeftPoint && mainRightPoint && minLeftIndex === minRightIndex) {
      return [mainLeftPoint, mainRightPoint];
    }

    // 圆弧隔墙相切接墙：端点恰在圆弧极点时两条偏移弧同向偏移——一条戳出墙外、与墙有真实交点(跨内)，
    // 另一条缩进、够不到墙(相切处本就求不到交点)。用三个点构成 L 形端盖：
    //   戳出侧用其与墙的真实交点(截到墙、保住墙厚) + 隔墙中心线在墙上的接触点(切点) + 够不到墙侧的自身偏移端点。
    // 端盖成「沿墙段 + 切线段」，外边算到与墙的交点而非夹到中心线，墙厚不被压窄。
    // 戳出侧的交点：两侧都有或都没有时置空，故它非空即等价于原来的「恰有一侧有交点」判据
    const outerCross = mainLeftPoint && mainRightPoint ? null : (mainLeftPoint ?? mainRightPoint);
    if (mainWallInfo.center && outerCross) {
      const cornerPoint = endOf(mainWallInfo.seg, type);
      // 缩进(够不到墙)的那条偏移边：戳出侧是 left 时缩进侧为 right，反之为 left。
      const recessedShape = (mainLeftPoint ? mainWallInfo.rightShape : mainWallInfo.leftShape) as Circle2d;
      const innerEnd = mainLeftPoint ? endOf(mainRightSeg, type) : endOf(mainLeftSeg, type);
      // innerEnd 与 cornerPoint 默认落在弧中线高度上；若该共享端点还点点连着一条有厚度的直隔墙
      // (透镜直径边)，这两个中线点会嵌入直墙体内。把它们抬到直墙朝向弧本体那一侧的边界(近边)上。
      const lifted = this.liftArcCapOntoTwinNearEdge(mainWallInfo, type, recessedShape, innerEnd, cornerPoint);
      return mainLeftPoint
        ? [outerCross, lifted.cornerPoint, lifted.innerEnd]
        : [lifted.innerEnd, lifted.cornerPoint, outerCross];
    }

    // 一侧有交点的情况，取端点边线再算一次
    let extraSeg: LineSeg2d | null = null; // 直角点到其他轮廓交点的线段
    if (mainLeftPoint && !mainRightPoint) {
      let direction = getTangentDirection(mainRightSeg, endOf(mainRightSeg, type));
      direction = Point2dUtils.rotateDirection(direction, Math.PI / 2);
      const mainRay = new Ray2d(endOf(mainRightSeg, type), direction);
      const intersect = rayIntersectSegments(mainRay, subWallShapeSegs);
      if (intersect) {
        mainRightPoint = mainRay.origin;
        minRightIndex = intersect.index;
        if (!Point2dUtils.equals(mainRightPoint, intersect.point)) {
          extraSeg = new LineSeg2d(intersect.point, mainRightPoint);
        }
      }
    } else if (mainRightPoint && !mainLeftPoint) {
      let direction = getTangentDirection(mainLeftSeg, endOf(mainLeftSeg, type));
      direction = Point2dUtils.rotateDirection(direction, -Math.PI / 2);
      const mainRay = new Ray2d(endOf(mainLeftSeg, type), direction);
      const intersect = rayIntersectSegments(mainRay, subWallShapeSegs);
      if (intersect) {
        mainLeftPoint = mainRay.origin;
        minLeftIndex = intersect.index;
        if (!Point2dUtils.equals(mainLeftPoint, intersect.point)) {
          extraSeg = new LineSeg2d(mainLeftPoint, intersect.point);
        }
      }
    }

    // 弧墙只在「两侧交点都没求到」时放弃；两侧都有但落在不同相邻墙边上时，往下走 PathFinder 拼复合端盖。
    if (mainWallInfo.center && !(mainLeftPoint && mainRightPoint)) {
      return null;
    }
    // 计算中间交点
    if (mainLeftPoint && mainRightPoint && minLeftIndex !== minRightIndex) {
      const segments = extraSeg ? [extraSeg, ...subWallShapeSegs] : subWallShapeSegs;
      const finder = new PathFinder({ segments });
      const [startPoint, endPoint] =
        type === HandleType.Start ? [mainLeftPoint, mainRightPoint] : [mainRightPoint, mainLeftPoint];
      extremeShapePath = finder.getPath(startPoint, endPoint);

      // 终点 需要进行反转(端点轮廓路径遵循left到right)
      if (type === HandleType.End) extremeShapePath = reverseShape(extremeShapePath);

      // 弧形主墙：两侧交点落在不同相邻墙边、但 PathFinder 在退化几何(相邻墙近相切重叠)下连不出路径时，
      // 直接用左右两个交点作端盖(两点直连)，而不是放弃返回未切的基础轮廓。
      if (mainWallInfo.center && (!extremeShapePath || extremeShapePath.length < 2)) {
        return [mainLeftPoint, mainRightPoint];
      }
    }

    if (!extremeShapePath) return null;

    {
      // 路径重复点处理
      const _points = [];
      for (let index = 0; index < extremeShapePath.length; index++) {
        if (
          index < extremeShapePath.length - 1 &&
          Point2dUtils.equals(extremeShapePath[index], extremeShapePath[index + 1])
        ) {
          index++;
          continue;
        }

        _points.push(extremeShapePath[index]);
      }
      extremeShapePath = _points;
    }

    if (extremeShapePath.length < 2) return null;

    return extremeShapePath;
  }

  // 弧端盖「贴墙 L 形」时，缩进边端点(innerEnd)与角点(cornerPoint)默认落在弧中线高度上；
  // 若该共享端点还点点相连着一条有厚度的直隔墙(透镜直径边)，这两个中线点会嵌入直墙体内。
  // 把它们抬到直墙「朝向弧本体(弧顶)那一侧」的边界(近边)上：
  //   · cornerPoint = 近边在共享端点那一端的端点(直墙端盖恰好落在房间墙处，外侧边沿房间墙上行)；
  //   · innerEnd    = 缩进边圆 ∩ 近边线，取离 cornerPoint 最近的交点。
  // 找不到这样的直墙(角点未嵌入任何相连直墙)时原样返回，不影响弧相切其它端盖(如仅接房间墙、无孪生直墙)。
  private liftArcCapOntoTwinNearEdge(
    mainWallInfo: IWallInfo,
    type: HandleType,
    recessedShape: Circle2d,
    innerEnd: IShapePoint2d,
    cornerPoint: IShapePoint2d
  ): { innerEnd: IShapePoint2d; cornerPoint: IShapePoint2d } {
    const sharedPoint = endOf(mainWallInfo.seg, type);
    const oppositePoint = farEndOf(mainWallInfo.seg, type);
    const connected = connectToPointOf(mainWallInfo, type);

    // 与本弧共用「另一端点」的平行孪生直隔墙(透镜直径边)、且角点(弧中线端点)嵌入其带内。
    // 必须限定为孪生：恰好路过该端点的其它直墙(如斜墙)不是直径边，不应据其偏转端盖。
    let twin: IWallInfo | undefined;
    for (const item of connected) {
      const info = this._cacheWallMap.get(item.id);
      if (!info || info.thickness <= 0 || info.center || !(info.shape instanceof Line2d)) continue;
      const isTwin =
        Point2dUtils.equals(info.seg.start, oppositePoint) || Point2dUtils.equals(info.seg.end, oppositePoint);
      if (!isTwin) continue;
      const dist = Math.abs(signedSideOfCenterline(cornerPoint, info.shape));
      if (dist < info.thickness / 2 - thresholdValue) {
        twin = info;
        break;
      }
    }
    if (!twin || !(twin.shape instanceof Line2d)) return { innerEnd, cornerPoint };

    // 近边 = 直墙朝向弧本体(弧顶)那一侧的偏移边
    const apex = (mainWallInfo.seg as ArcSeg2d).getMidPoint();
    const nearSeg = nearOffsetSegOf(twin, twin.shape, apex);
    const nearLine = extendSeg(nearSeg);

    // cornerPoint = 近边在共享端点那一端的端点
    const newCorner = Point2dUtils.equals(twin.seg.start, sharedPoint) ? nearSeg.start : nearSeg.end;

    // innerEnd = 缩进边圆 ∩ 近边线，取离角点最近的交点
    const newInner = getNearestPoint(intersectCommonShape(recessedShape, nearLine) || [], newCorner) ?? innerEnd;
    return { innerEnd: newInner, cornerPoint: newCorner };
  }

  clear() {
    this._cacheWallMap.clear();
    this._buildOrder.clear();
    this._pointPointConnectedQueue = [];
    this._pointWallConnectedQueue = [];
    this._result = [];
  }
}

function sortAndRemoveDuplicates(items: IUpdateItem[]) {
  return items
    .sort((a, b) => (a.wallIndex - b.wallIndex !== 0 ? a.wallIndex - b.wallIndex : a.type > b.type ? 1 : -1))
    .filter((item, index) => {
      const prevItem = items[index - 1];
      return index === 0 || !(item.wallIndex === prevItem.wallIndex && item.type === prevItem.type);
    });
}
